import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import ConfirmModal from '../components/ConfirmModal';
import CancelBookingModal from '../components/CancelBookingModal';
import PlaceSearch from '../components/PlaceSearch';
import { formatRoles } from '../utils/roleUtils';
import { haversineKm } from '../utils/distance';
import GoogleMap from '../components/GoogleMap';
import { useAuth } from '../contexts/AuthContext';
import {
  getBookings,
  createBooking,
  updateBooking,
  deleteBooking,
  cancelPilotAssignment,
  Booking,
  getPatients,
  Patient,
  getAmbulances,
  Ambulance,
  getUsers,
  User,
  getTenants,
  Tenant,
  getTenant,
  getAttendance,
  Attendance,
  createPatient,
  downloadBookingReportPDF,
  downloadBookingReportExcel,
  downloadBookingDetailsPDF,
  downloadBookingDetailsExcel,
  getPatientVitalFields,
  PatientVitalFields,
  getAmbulanceTypes,
  AmbulanceType,
  calculateBookingAmount as calculateBookingAmountApi,
  getAmbulanceLiveLocation,
  getAmbulanceLiveLocationByDeviceId,
} from '../services/api';
import { BOOKING_TYPE_LABELS, BOOKING_TYPE_COLORS } from '../constants/booking';
import { formatDateTimeDDMMYYYY, formatDateDDMMYYYY, getMaxDateToday, getMaxDateFuture, getMaxEndDateForStart } from '../utils/dateUtils';


const BookingManagement: React.FC = () => {
  const { user, hasPermission, isTenantAdmin, isSystemAdmin } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelBookingConfirm, setShowCancelBookingConfirm] = useState(false);
  const [showCancelPilotAssignmentConfirm, setShowCancelPilotAssignmentConfirm] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignMode, setAssignMode] = useState<'assign' | 'reassign'>('assign');
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showBookingMapModal, setShowBookingMapModal] = useState(false);
  const [mapAmbulanceLocation, setMapAmbulanceLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<{ id: string; name: string } | null>(null);
  const [bookingToCancel, setBookingToCancel] = useState<string | null>(null);
  const [pilotAssignmentToCancel, setPilotAssignmentToCancel] = useState<string | null>(null);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [assigningBooking, setAssigningBooking] = useState<Booking | null>(null);
  const [listError, setListError] = useState('');
  const [formError, setFormError] = useState('');
  const [assignError, setAssignError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{
    patientId?: string;
    pickupLocation?: string;
    dropoffLocation?: string;
    scheduledPickupTime?: string;
    newPatientName?: string;
    newPatientPhone?: string;
    newPatientGender?: string;
    newPatientDob?: string;
    newPatientContactPersonName?: string;
    newPatientContactPersonPhone?: string;
  }>({});
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [bookingTypeFilter, setBookingTypeFilter] = useState('');
  const [patientPhoneError, setPatientPhoneError] = useState('');
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string>('');
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef<HTMLDivElement>(null);
  const [detailsDownloading, setDetailsDownloading] = useState(false);
  const [detailsDownloadError, setDetailsDownloadError] = useState<string>('');
  const [showDetailsDownloadMenu, setShowDetailsDownloadMenu] = useState(false);
  const detailsDownloadMenuRef = useRef<HTMLDivElement>(null);
  const [vitalFields, setVitalFields] = useState<PatientVitalFields | null>(null);
  const [showVitalInfo, setShowVitalInfo] = useState(false);
  const [createBookingSubmitting, setCreateBookingSubmitting] = useState(false);
  const [assignSubmitting, setAssignSubmitting] = useState(false);

  // Ambulance type state for tariff-based booking amount calculation
  const [ambulanceTypesForForm, setAmbulanceTypesForForm] = useState<Array<{ _id: string; name: string; displayName: string; iconUrl?: string }>>([]);
  const [calculatedDistance, setCalculatedDistance] = useState<number | null>(null);
  const [isCalculatingAmount, setIsCalculatingAmount] = useState(false);
  const [bookingAmountManuallyEdited, setBookingAmountManuallyEdited] = useState(false);

  // Create form ambulance selection state
  const [selectedCreateAmbulanceId, setSelectedCreateAmbulanceId] = useState<string>('');
  const [showCreateAmbulanceDropdown, setShowCreateAmbulanceDropdown] = useState(false);
  const [createAmbulanceSearchTerm, setCreateAmbulanceSearchTerm] = useState('');
  const createAmbulanceDropdownRef = useRef<HTMLDivElement>(null);

  // Live location state: map of deviceId (lowercase) -> { latitude, longitude }
  const [ambulanceLiveLocations, setAmbulanceLiveLocations] = useState<Record<string, { latitude: number; longitude: number }>>({});

  // Date filter state (similar to Reports Attendance)
  type DateFilterType = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'tomorrow' | 'next7days' | 'custom' | 'all';
  const [dateFilterType, setDateFilterType] = useState<DateFilterType>('today');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  // Form data for create/edit
  const [formData, setFormData] = useState({
    name: '',
    patientId: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'critical',
    bookingType: 'to_hospital' as 'to_hospital' | 'from_hospital',
    medicalCondition: '',
    notes: '',
    pickupLocation: null as { latitude: number; longitude: number; address?: string } | null,
    pickupAddressDetails: '', // Flat/Area/Landmark for pickup
    dropoffLocation: null as { latitude: number; longitude: number; address?: string } | null,
    dropoffAddressDetails: '', // Flat/Area/Landmark for dropoff
    scheduledPickupTime: '',
    // New patient fields
    createNewPatient: false,
    patientName: '',
    patientPhone: '',
    patientGender: '',
    patientDateOfBirth: '',
    patientIdNumber: '',
    patientContactPersonName: '',
    patientContactPersonPhone: '',
    vitalInfo: {} as Record<string, string | number>,
    bookingAmount: '',
  });

  // Monitor theme changes
  useEffect(() => {
    const checkTheme = () => {
      setIsDarkTheme(document.documentElement.getAttribute('data-theme') === 'dark');
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // calculateDistanceKm → haversineKm (from ../utils/distance)
  const calculateDistanceKm = haversineKm;

  // Fetch ambulance types for the form dropdown
  const fetchAmbulanceTypesForForm = async () => {
    try {
      const response = await getAmbulanceTypes(1, 200);
      const types = (response.data.data || []).map((t: any) => ({
        _id: t._id,
        name: t.metadata.name,
        displayName: t.metadata.displayName || t.metadata.name,
        iconUrl: t.spec?.iconUrl,
      }));
      setAmbulanceTypesForForm(types);
    } catch (err) {
      console.error('Failed to load ambulance types for form:', err);
    }
  };

  // Helper to resolve ambulance type name to displayName
  const getAmbulanceTypeDisplayName = (typeName: string): string => {
    const matched = ambulanceTypesForForm.find(
      (t) => t.name === typeName || t.displayName === typeName
    );
    return matched?.displayName || typeName;
  };

  // Fetch live locations for all ambulances from external API
  const fetchAmbulanceLiveLocations = async () => {
    try {
      if (!user?.tenantId) return;
      const tenantResponse = await getTenant(user.tenantId);
      const gId = tenantResponse.data?.data?.spec?.groupId;
      if (!gId) {
        console.warn('No groupId found in tenant data for live locations');
        return;
      }
      const response = await getAmbulanceLiveLocation(gId);
      const data = response.data;
      let locations: any[] = [];
      if (Array.isArray(data)) {
        locations = data;
      } else if (data?.data && Array.isArray(data.data)) {
        locations = data.data;
      } else if (data?.ambulances && Array.isArray(data.ambulances)) {
        locations = data.ambulances;
      }

      const locMap: Record<string, { latitude: number; longitude: number }> = {};
      locations.forEach((loc: any) => {
        const liveLoc = loc.liveLocation;
        if (!Array.isArray(liveLoc) || liveLoc.length !== 2) return;
        const [lng, lat] = liveLoc;
        if (lng === 0 && lat === 0) return;
        if (isNaN(lng) || isNaN(lat)) return;
        const key = (loc.deviceId || loc.id || '').toLowerCase();
        if (key) {
          locMap[key] = { latitude: lat, longitude: lng };
        }
      });
      setAmbulanceLiveLocations(locMap);
    } catch (err) {
      console.error('Failed to fetch ambulance live locations:', err);
    }
  };

  // Helper to get location for an ambulance: prefer live location, fallback to spec.currentLocation
  const getAmbulanceLiveLocation_ = (ambulance: Ambulance): { latitude: number; longitude: number } | null => {
    // First try live location via GPS device
    const deviceId = ambulance.spec?.gpsDevice?.deviceId;
    if (deviceId) {
      const liveLoc = ambulanceLiveLocations[deviceId.toLowerCase()];
      if (liveLoc) return liveLoc;
    }
    // Fallback to stored currentLocation
    if (ambulance.spec?.currentLocation?.latitude && ambulance.spec?.currentLocation?.longitude) {
      return { latitude: ambulance.spec.currentLocation.latitude, longitude: ambulance.spec.currentLocation.longitude };
    }
    return null;
  };

  // Auto-calculate booking amount when ambulance + locations change
  useEffect(() => {
    if (bookingAmountManuallyEdited) return;
    if (!selectedCreateAmbulanceId) return;
    if (!formData.pickupLocation || !formData.dropoffLocation) return;

    // Get selected ambulance and its type
    const selectedAmb = ambulances.find((a) => a._id === selectedCreateAmbulanceId);
    if (!selectedAmb?.spec?.type) return;

    const typeName = selectedAmb.spec.type;
    const matchedType = ambulanceTypesForForm.find(
      (t) => t.displayName === typeName || t.name === typeName
    );
    if (!matchedType) return;

    // Calculate total distance: ambulance live location → pickup + pickup → dropoff
    let totalDistance = 0;
    const ambLiveLocation = getAmbulanceLiveLocation_(selectedAmb);
    if (ambLiveLocation) {
      totalDistance += calculateDistanceKm(
        ambLiveLocation.latitude,
        ambLiveLocation.longitude,
        formData.pickupLocation.latitude,
        formData.pickupLocation.longitude
      );
    }
    totalDistance += calculateDistanceKm(
      formData.pickupLocation.latitude,
      formData.pickupLocation.longitude,
      formData.dropoffLocation.latitude,
      formData.dropoffLocation.longitude
    );
    const roundedDistance = Math.round(totalDistance * 100) / 100;
    setCalculatedDistance(roundedDistance);

    // Call API to calculate amount
    setIsCalculatingAmount(true);
    calculateBookingAmountApi(matchedType._id, roundedDistance)
      .then((res) => {
        const amount = res.data?.data?.amount;
        if (amount !== null && amount !== undefined) {
          setFormData((prev) => ({ ...prev, bookingAmount: String(amount) }));
        }
      })
      .catch((err) => {
        console.error('Failed to calculate booking amount:', err);
      })
      .finally(() => {
        setIsCalculatingAmount(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCreateAmbulanceId, formData.pickupLocation, formData.dropoffLocation, bookingAmountManuallyEdited, ambulanceTypesForForm, ambulanceLiveLocations]);

  // Close download menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
        setShowDownloadMenu(false);
      }
    };

    if (showDownloadMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDownloadMenu]);

  // Close booking-details download menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        detailsDownloadMenuRef.current &&
        !detailsDownloadMenuRef.current.contains(event.target as Node)
      ) {
        setShowDetailsDownloadMenu(false);
      }
    };

    if (showDetailsDownloadMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDetailsDownloadMenu]);

  // Close drawer with Escape key
  useEffect(() => {
    if (!showModal && !showAssignModal && !showDetailsDrawer) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement && typeof activeElement.blur === 'function') {
          activeElement.blur();
        }
        if (showDetailsDrawer) {
          setShowDetailsDrawer(false);
        } else if (showAssignModal) {
          setShowAssignModal(false);
        } else if (showModal) {
          setShowModal(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal, showAssignModal, showDetailsDrawer]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (showModal || showAssignModal) {
      // Save current scroll position
      const scrollY = window.scrollY;
      // Lock body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        // Restore scroll position
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showModal, showAssignModal]);

  // Assignment form data
  const [assignFormData, setAssignFormData] = useState({
    ambulanceId: '',
    pilotId: '',
    assignedStaffIds: [] as string[],
    bookingType: 'to_hospital' as 'to_hospital' | 'from_hospital',
    bookingAmount: '',
  });
  const [assignAmountManuallyEdited, setAssignAmountManuallyEdited] = useState(false);
  const [assignAmountCalculating, setAssignAmountCalculating] = useState(false);
  const [assignCalculatedDistance, setAssignCalculatedDistance] = useState<number | null>(null);

  // Dropdown data
  const [patients, setPatients] = useState<Patient[]>([]);
  const [ambulances, setAmbulances] = useState<Ambulance[]>([]);
  const [staff, setStaff] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [todayAttendance, setTodayAttendance] = useState<Attendance[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [pilotSearchTerm, setPilotSearchTerm] = useState('');
  const [staffSearchTerm, setStaffSearchTerm] = useState('');
  const [ambulanceSearchTerm, setAmbulanceSearchTerm] = useState('');
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [hospitalDefaultLocation, setHospitalDefaultLocation] = useState<{ latitude: number; longitude: number; address?: string } | null>(null);
  const [showAmbulanceDropdown, setShowAmbulanceDropdown] = useState(false);
  const [showPilotDropdown, setShowPilotDropdown] = useState(false);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const patientDropdownRef = useRef<HTMLDivElement>(null);
  const [debouncedStaffSearchTerm, setDebouncedStaffSearchTerm] = useState('');
  const [displayedStaffLimit, setDisplayedStaffLimit] = useState(50);

  // Fetch vital fields
  const fetchVitalFields = async () => {
    try {
      const tenantId = user?.isSystem ? undefined : user?.tenantId;
      const response = await getPatientVitalFields(tenantId);
      setVitalFields(response.data.data);
    } catch (err: any) {
      console.error('Failed to load patient vital fields:', err);
    }
  };

  // Fetch staff and tenants once on mount (for getBookedByName and other functions)
  useEffect(() => {
    fetchStaff();
    fetchTenants();
    fetchVitalFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset page to 1 and fetch bookings when filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchBookings(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter, bookingTypeFilter, dateFilterType, customStartDate, customEndDate]);

  // Debounce search term - wait 800ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Debounce staff search term for API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedStaffSearchTerm(staffSearchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [staffSearchTerm]);

  // Fetch staff when debounced search changes
  useEffect(() => {
    if (showAssignModal) {
      fetchStaff(debouncedStaffSearchTerm || undefined);
      setDisplayedStaffLimit(50); // Reset limit when search changes
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedStaffSearchTerm, showAssignModal]);

  // Auto-calculate booking amount in assign modal when ambulance changes
  useEffect(() => {
    if (!showAssignModal || !assigningBooking) return;
    if (assignAmountManuallyEdited) return;
    if (!assignFormData.ambulanceId) return;

    // Get ambulance type from the selected ambulance
    const selectedAmb = ambulances.find((a) => a._id === assignFormData.ambulanceId);
    if (!selectedAmb?.spec?.type) return;

    // Find ambulance type ID from the name/displayName
    const typeName = selectedAmb.spec.type;
    const matchedType = ambulanceTypesForForm.find(
      (t) => t.displayName === typeName || t.name === typeName
    );
    if (!matchedType) return;

    // Calculate total distance: ambulance live location → pickup + pickup → dropoff
    const pickup = assigningBooking.spec.pickupLocation;
    const dropoff = assigningBooking.spec.dropoffLocation;
    if (!pickup || !dropoff) return;

    let totalDistance = 0;
    const ambLiveLocation = getAmbulanceLiveLocation_(selectedAmb);
    if (ambLiveLocation) {
      totalDistance += calculateDistanceKm(
        ambLiveLocation.latitude,
        ambLiveLocation.longitude,
        pickup.latitude, pickup.longitude
      );
    }
    totalDistance += calculateDistanceKm(
      pickup.latitude, pickup.longitude,
      dropoff.latitude, dropoff.longitude
    );
    const roundedDistance = Math.round(totalDistance * 100) / 100;
    setAssignCalculatedDistance(roundedDistance);

    // Call API to calculate amount
    setAssignAmountCalculating(true);
    calculateBookingAmountApi(matchedType._id, roundedDistance)
      .then((res) => {
        const amount = res.data?.data?.amount;
        if (amount !== null && amount !== undefined) {
          setAssignFormData((prev) => ({ ...prev, bookingAmount: String(amount) }));
        }
      })
      .catch((err) => {
        console.error('Failed to calculate booking amount in assign:', err);
      })
      .finally(() => {
        setAssignAmountCalculating(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignFormData.ambulanceId, showAssignModal, assignAmountManuallyEdited, ambulanceTypesForForm, ambulanceLiveLocations]);

  // Reset page to 1 and refetch bookings when debounced search changes
  useEffect(() => {
    setCurrentPage(1);
    fetchBookings(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const fetchBookings = async (pageOverride?: number, pageSizeOverride?: number) => {
    try {
      setLoading(true);
      setListError(''); // Clear previous errors
      const pageToUse = pageOverride ?? currentPage;
      const sizeToUse = pageSizeOverride ?? pageSize;
      const params: any = { page: pageToUse, pageSize: sizeToUse };
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (bookingTypeFilter) params.bookingType = bookingTypeFilter;
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;

      // Helper function to format date in YYYY-MM-DD format using local timezone
      const formatDateLocal = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Calculate date range based on filter type (mirroring Reports Attendance behaviour)
      const getDateRange = () => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        switch (dateFilterType) {
          case 'today': {
            const start = new Date(today);
            start.setHours(0, 0, 0, 0);
            const end = new Date(today);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end)
            };
          }
          case 'yesterday': {
            const start = new Date(today);
            start.setDate(start.getDate() - 1);
            start.setHours(0, 0, 0, 0);
            const end = new Date(start);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end)
            };
          }
          case 'thisWeek': {
            const start = new Date(today);
            const dayOfWeek = start.getDay();
            const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
            start.setDate(diff);
            start.setHours(0, 0, 0, 0);
            const end = new Date(today);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end)
            };
          }
          case 'lastWeek': {
            const start = new Date(today);
            const dayOfWeek = start.getDay();
            const diff = start.getDate() - dayOfWeek - 6;
            start.setDate(diff);
            start.setHours(0, 0, 0, 0);
            const end = new Date(start);
            end.setDate(end.getDate() + 6);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end)
            };
          }
          case 'thisMonth': {
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            start.setHours(0, 0, 0, 0);
            const end = new Date(today);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end),
            };
          }
          case 'lastMonth': {
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            start.setHours(0, 0, 0, 0);
            const end = new Date(now.getFullYear(), now.getMonth(), 0);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end),
            };
          }
          case 'tomorrow': {
            const start = new Date(today);
            start.setDate(start.getDate() + 1);
            start.setHours(0, 0, 0, 0);
            const end = new Date(start);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end),
            };
          }
          case 'next7days': {
            const start = new Date(today);
            start.setDate(start.getDate() + 1);
            start.setHours(0, 0, 0, 0);
            const end = new Date(today);
            end.setDate(end.getDate() + 7);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end),
            };
          }
          case 'custom': {
            if (!customStartDate || !customEndDate) {
              return { startDate: undefined, endDate: undefined };
            }
            const normalizeDate = (dateInput: string): string => {
              const trimmed = dateInput.trim();
              if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
              return trimmed;
            };
            const startStr = normalizeDate(customStartDate);
            const endStr = normalizeDate(customEndDate);
            const maxEndStr = getMaxEndDateForStart(startStr);
            const endDateClamped = endStr && maxEndStr && endStr > maxEndStr ? maxEndStr : endStr;
            return {
              startDate: startStr,
              endDate: endDateClamped,
            };
          }
          case 'all':
            return { startDate: undefined, endDate: undefined };
          default:
            return { startDate: undefined, endDate: undefined };
        }
      };

      const dateRange = getDateRange();
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;

      // On initial load with 'today' filter, check if there are bookings for today
      let response;
      if (isInitialLoad && dateFilterType === 'today') {
        // First, try to fetch today's bookings (just check if any exist)
        const todayResponse = await getBookings(
          1,
          1, // Just check if any exist
          params.status,
          undefined,
          undefined,
          params.priority,
          params.search,
          params.startDate,
          params.endDate,
          params.bookingType
        );

        // If no bookings for today, fetch last records without date filter
        if (!todayResponse.data.data || todayResponse.data.data.length === 0) {
          setDateFilterType('all');
          params.startDate = undefined;
          params.endDate = undefined;
          params.page = 1;
          params.pageSize = sizeToUse;
          // Fetch records without date filter
          response = await getBookings(
            params.page,
            params.pageSize,
            params.status,
            undefined,
            undefined,
            params.priority,
            params.search,
            params.startDate,
            params.endDate,
            params.bookingType
          );
        } else {
          // There are bookings for today, use the response we already got but fetch full page
          params.page = pageToUse;
          params.pageSize = sizeToUse;
          response = await getBookings(
            params.page,
            params.pageSize,
            params.status,
            undefined,
            undefined,
            params.priority,
            params.search,
            params.startDate,
            params.endDate,
            params.bookingType
          );
        }
        setIsInitialLoad(false);
      } else {
        // Normal fetch (not initial load or not 'today' filter)
        response = await getBookings(
          params.page,
          params.pageSize,
          params.status,
          undefined,
          undefined,
          params.priority,
          params.search,
          params.startDate,
          params.endDate,
          params.bookingType
        );
      }
      setBookings(response.data.data || []);
      const pagination = response.data.pagination;
      if (pagination) {
        setCurrentPage(pagination.currentPage || pageToUse);
        setPageSize(pagination.pageSize || sizeToUse);
        setTotalItems(pagination.totalItems || 0);
        setHasNextPage(pagination.hasNextPage || false);
        setHasPreviousPage(pagination.hasPreviousPage || false);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load bookings';
      setListError(errorMessage);
      console.error('Failed to load bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleRefresh = () => {
    fetchBookings();
  };

  const handlePageChange = (page: number) => {
    fetchBookings(page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    fetchBookings(1, size);
  };

  const fetchPatients = async () => {
    try {
      const response = await getPatients(1, 1000);
      setPatients(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load patients:', err);
    }
  };


  const fetchAmbulances = async () => {
    try {
      const response = await getAmbulances(1, 1000);
      setAmbulances(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load ambulances:', err);
    }
  };

  const fetchStaff = async (search?: string) => {
    try {
      // Fetch more users if searching, otherwise limit to reasonable number
      const pageSize = search ? 500 : 200;
      const response = await getUsers(1, pageSize, search);
      setStaff(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load staff:', err);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await getTenants(1, 1000);
      setTenants(response.data.data || []);
    } catch (err: any) {
      // Non-admins often lack list access — fall back to own tenant for booking flows
      console.warn('Failed to load tenants list, trying own tenant:', err?.response?.status || err);
      if (user?.tenantId) {
        try {
          const own = await getTenant(user.tenantId);
          if (own.data?.data) {
            setTenants([own.data.data]);
            return;
          }
        } catch (ownErr) {
          console.error('Failed to load own tenant:', ownErr);
        }
      }
      setTenants([]);
    }
  };

  // Helper function to get booked by name
  const getBookedByName = (bookedBy: string, bookedByDisplay?: string): string => {
    if (bookedByDisplay) {
      return bookedByDisplay;
    }
    if (!bookedBy) {
      return '—';
    }
    // First, try to find in staff/users
    const staffMember = staff.find((s) => (s._id || s.id) === bookedBy);
    if (staffMember) {
      return staffMember.metadata?.name || staffMember.name || bookedBy;
    }

    // If not found, try to find in tenants (for tenant admin bookings)
    const tenant = tenants.find((t) => t._id === bookedBy);
    if (tenant) {
      return tenant.metadata?.name || bookedBy;
    }

    // If still not found, return the ID (shouldn't happen, but fallback)
    return bookedBy;
  };

  const fetchTodayAttendance = async () => {
    try {
      // Fetch latest checked-in records (no date constraint)
      const response = await getAttendance(1, 1000, undefined, undefined, undefined, 'checkedIn');
      setTodayAttendance(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load today\'s attendance:', err);
      setTodayAttendance([]);
    }
  };

  // Get date range for API call (returns timestamps)
  const getDateRangeForAPI = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateFilterType) {
      case 'today': {
        const start = new Date(today);
        start.setHours(0, 0, 0, 0);
        const end = new Date(today);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'yesterday': {
        const start = new Date(today);
        start.setDate(start.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'thisWeek': {
        const start = new Date(today);
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        const end = new Date(today);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'lastWeek': {
        const start = new Date(today);
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek - 6;
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'thisMonth': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(today);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'lastMonth': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'tomorrow': {
        const start = new Date(today);
        start.setDate(start.getDate() + 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'next7days': {
        const start = new Date(today);
        start.setDate(start.getDate() + 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(today);
        end.setDate(end.getDate() + 7);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'custom': {
        if (!customStartDate || !customEndDate) {
          return null;
        }
        const start = new Date(customStartDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(customEndDate);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'all': {
        // For 'all', use a wide date range (last 2 years to today)
        const end = new Date();
        end.setHours(23, 59, 59, 999);
        const start = new Date();
        start.setFullYear(start.getFullYear() - 2);
        start.setHours(0, 0, 0, 0);
        return { start: start.getTime(), end: end.getTime() };
      }
      default:
        return null;
    }
  };

  const handleDownload = async (format: 'pdf' | 'excel') => {
    try {
      setDownloading(true);
      setDownloadError('');
      setShowDownloadMenu(false);

      const dateRange = getDateRangeForAPI();
      if (!dateRange) {
        setDownloadError('Please select a valid date range');
        setDownloading(false);
        return;
      }

      // Pass all active filters to the download API
      let response;
      let fileExtension;

      if (format === 'pdf') {
        response = await downloadBookingReportPDF(
          dateRange.start,
          dateRange.end,
          statusFilter || undefined,
          priorityFilter || undefined,
          bookingTypeFilter || undefined,
          debouncedSearchTerm || undefined
        );
        fileExtension = 'pdf';
      } else {
        response = await downloadBookingReportExcel(
          dateRange.start,
          dateRange.end,
          statusFilter || undefined,
          priorityFilter || undefined,
          bookingTypeFilter || undefined,
          debouncedSearchTerm || undefined
        );
        fileExtension = 'xlsx';
      }

      const { url } = response.data.data;

      // Create a temporary anchor element to download the file
      const link = document.createElement('a');
      link.href = url;
      const dateRangeStr = dateFilterType === 'custom'
        ? `${customStartDate}_to_${customEndDate}`
        : dateFilterType;
      link.download = `booking-report-${dateRangeStr}.${fileExtension}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error(`Error downloading ${format.toUpperCase()}:`, err);
      setDownloadError(err.response?.data?.message || `Failed to download ${format.toUpperCase()}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadBookingDetails = async (format: 'pdf' | 'excel') => {
    if (!selectedBooking) return;

    try {
      setDetailsDownloading(true);
      setDetailsDownloadError('');
      setShowDetailsDownloadMenu(false);

      const bookingId = (selectedBooking as any)._id || (selectedBooking as any).id;
      if (!bookingId) {
        setDetailsDownloadError('Booking ID is missing for download');
        return;
      }

      let response;
      if (format === 'pdf') {
        response = await downloadBookingDetailsPDF(bookingId);
        const { url } = response.data.data;
        // Open PDF in a new tab for viewing
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      } else {
        response = await downloadBookingDetailsExcel(bookingId);
      }

      const { url, fileName } = response.data.data;
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'booking-details.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error(`Error downloading booking details ${format.toUpperCase()}:`, err);
      setDetailsDownloadError(
        err?.response?.data?.message || `Failed to download booking details ${format.toUpperCase()}`
      );
    } finally {
      setDetailsDownloading(false);
    }
  };

  const handleOpenModal = async (booking?: Booking) => {
    if (booking) {
      setEditingBooking(booking);

      setFormData({
        name: booking.metadata.name,
        patientId: booking.spec.patientId,
        priority: booking.spec.priority,
        bookingType: booking.spec.bookingType || 'to_hospital',
        medicalCondition: booking.spec.medicalCondition || '',
        notes: booking.spec.notes || '',
        pickupLocation: booking.spec.pickupLocation || null,
        pickupAddressDetails: '', // Will be extracted if needed, or left empty for now
        dropoffLocation: booking.spec.dropoffLocation || null,
        dropoffAddressDetails: '', // Will be extracted if needed, or left empty for now
        scheduledPickupTime: booking.spec.timings?.scheduledPickupTime
          ? new Date(booking.spec.timings.scheduledPickupTime).toISOString().slice(0, 16)
          : '',
        createNewPatient: false,
        patientName: '',
        patientPhone: '',
        patientGender: '',
        patientDateOfBirth: '',
        patientIdNumber: '',
        patientContactPersonName: '',
        patientContactPersonPhone: '',
        vitalInfo: {},
        bookingAmount: booking.spec.bookingAmount || '',
      });
    } else {
      setEditingBooking(null);

      // Set default dropoff location to hospital location
      let defaultDropoffLocation = null;
      if (user?.tenantId) {
        defaultDropoffLocation = await setDefaultHospitalLocation(user.tenantId);
        if (defaultDropoffLocation) {
          setHospitalDefaultLocation(defaultDropoffLocation);
        }
      }

      // Set default scheduled pickup time to current time (in local timezone)
      const now = new Date();
      // Format date in local timezone for datetime-local input
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const defaultScheduledTime = `${year}-${month}-${day}T${hours}:${minutes}`;

      setFormData({
        name: '',
        patientId: '',
        priority: 'medium',
        bookingType: 'to_hospital',
        medicalCondition: '',
        notes: '',
        pickupLocation: null,
        pickupAddressDetails: '',
        dropoffLocation: defaultDropoffLocation,
        dropoffAddressDetails: '',
        scheduledPickupTime: defaultScheduledTime,
        createNewPatient: false,
        patientName: '',
        patientPhone: '',
        patientGender: '',
        patientDateOfBirth: '',
        patientIdNumber: '',
        patientContactPersonName: '',
        patientContactPersonPhone: '',
        vitalInfo: {},
        bookingAmount: '',
      });
    }
    setShowModal(true);
    setShowVitalInfo(false);
    // Reset ambulance + auto-calculation state
    setSelectedCreateAmbulanceId('');
    setShowCreateAmbulanceDropdown(false);
    setCreateAmbulanceSearchTerm('');
    setCalculatedDistance(null);
    setBookingAmountManuallyEdited(false);
    setIsCalculatingAmount(false);
    // Fetch patients, ambulances, ambulance types, and live locations when modal opens
    fetchPatients();
    fetchAmbulances();
    fetchAmbulanceTypesForForm();
    fetchAmbulanceLiveLocations();
    setFormError('');
    setFieldErrors({});
  };

  const handleOpenAssignModal = (booking: Booking, mode: 'assign' | 'reassign' = 'assign') => {
    setAssignMode(mode);
    setAssigningBooking(booking);
    setAssignFormData({
      ambulanceId: booking.spec.ambulanceId || '',
      pilotId: booking.spec.pilotId || '',
      assignedStaffIds: booking.spec.assignedStaffIds || [],
      bookingType: booking.spec.bookingType || 'to_hospital',
      bookingAmount: booking.spec.bookingAmount || '',
    });
    setAssignAmountManuallyEdited(false);
    setAssignAmountCalculating(false);
    setAssignCalculatedDistance(null);
    setPilotSearchTerm('');
    setStaffSearchTerm('');
    setDebouncedStaffSearchTerm('');
    setDisplayedStaffLimit(50);
    setShowAssignModal(true);
    fetchTodayAttendance();
    setAssignError('');
    // Fetch staff, ambulances, ambulance types, and live locations when modal opens
    fetchStaff();
    fetchAmbulances();
    fetchAmbulanceTypesForForm();
    fetchAmbulanceLiveLocations();
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setAssigningBooking(null);
    setAssignMode('assign');
    setAmbulanceSearchTerm('');
    setShowAmbulanceDropdown(false);
    setPilotSearchTerm('');
    setShowPilotDropdown(false);
    setStaffSearchTerm('');
    setDebouncedStaffSearchTerm('');
    setDisplayedStaffLimit(50);
    setAssignError('');
  };

  // Refs for dropdowns
  const ambulanceDropdownRef = useRef<HTMLDivElement>(null);
  const pilotDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ambulanceDropdownRef.current && !ambulanceDropdownRef.current.contains(event.target as Node)) {
        setShowAmbulanceDropdown(false);
      }
      if (pilotDropdownRef.current && !pilotDropdownRef.current.contains(event.target as Node)) {
        setShowPilotDropdown(false);
      }
      if (patientDropdownRef.current && !patientDropdownRef.current.contains(event.target as Node)) {
        setShowPatientDropdown(false);
      }
      if (createAmbulanceDropdownRef.current && !createAmbulanceDropdownRef.current.contains(event.target as Node)) {
        setShowCreateAmbulanceDropdown(false);
      }
    };

    if (showAmbulanceDropdown || showPilotDropdown || showPatientDropdown || showCreateAmbulanceDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAmbulanceDropdown, showPilotDropdown, showPatientDropdown, showCreateAmbulanceDropdown]);

  // Geocode hospital address to set as default dropoff location using Google Geocoding API
  const geocodeHospitalAddress = async (address: string): Promise<{ latitude: number; longitude: number; address: string } | null> => {
    if (!address || !address.trim()) return null;

    // Wait for Google Maps to load
    if (!window.google || !window.google.maps) {
      return new Promise((resolve) => {
        const checkGoogleMaps = setInterval(() => {
          if (window.google && window.google.maps) {
            clearInterval(checkGoogleMaps);
            geocodeAddress(address).then(resolve);
          }
        }, 100);
        setTimeout(() => {
          clearInterval(checkGoogleMaps);
          resolve(null);
        }, 5000);
      });
    }

    return geocodeAddress(address);
  };

  const geocodeAddress = async (address: string): Promise<{ latitude: number; longitude: number; address: string } | null> => {
    return new Promise((resolve) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: address }, (results, status) => {
        if (status === 'OK' && results && results[0] && results[0].geometry && results[0].geometry.location) {
          const location = results[0].geometry.location;
          resolve({
            latitude: location.lat(),
            longitude: location.lng(),
            address: results[0].formatted_address || address,
          });
        } else {
          console.error('Geocoding failed:', status);
          resolve(null);
        }
      });
    });
  };

  const reverseGeocode = async (lat: number, lng: number): Promise<string | null> => {
    if (!window.google || !window.google.maps) return null;
    return new Promise((resolve) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          resolve(results[0].formatted_address || null);
        } else {
          resolve(null);
        }
      });
    });
  };

  // Set default dropoff location from tenant's hospital
  const setDefaultHospitalLocation = async (tenantId: string | undefined): Promise<{ latitude: number; longitude: number; address: string } | null> => {
    if (!tenantId) return null;

    try {
      // Try to find tenant in already fetched tenants list first
      let tenant = tenants.find((t) => t._id === tenantId);

      // If not found, fetch it
      if (!tenant) {
        const response = await getTenant(tenantId);
        if (response.data.success && response.data.data) {
          tenant = response.data.data;
        }
      }

      if (tenant?.spec?.hospital) {
        const hospital = tenant.spec.hospital;

        // First, check if hospital has saved coordinates (from tenant settings)
        // Check hospitalLocation first (preferred), then fallback to direct latitude/longitude
        const hospitalLat = hospital.hospitalLocation?.latitude ?? hospital.latitude;
        const hospitalLng = hospital.hospitalLocation?.longitude ?? hospital.longitude;

        if (hospitalLat !== undefined && hospitalLng !== undefined) {
          // Build address string for display
          const addressParts = [
            hospital.name,
            hospital.address,
            hospital.city,
            hospital.state,
            hospital.zipCode,
            hospital.country,
          ].filter(Boolean);

          return {
            latitude: hospitalLat,
            longitude: hospitalLng,
            address: addressParts.join(', ') || 'Hospital Location',
          };
        }

        // If no coordinates, try to geocode from address
        const addressParts = [
          hospital.name,
          hospital.address,
          hospital.city,
          hospital.state,
          hospital.zipCode,
          hospital.country,
        ].filter(Boolean);

        if (addressParts.length > 0) {
          const hospitalAddress = addressParts.join(', ');
          // Geocode the hospital address
          return await geocodeHospitalAddress(hospitalAddress);
        }
      }
    } catch (error) {
      console.error('Error setting default hospital location:', error);
    }
    return null;
  };

  const ensureHospitalDefaultLocation = async (): Promise<{ latitude: number; longitude: number; address?: string } | null> => {
    if (hospitalDefaultLocation) return hospitalDefaultLocation;
    if (!user?.tenantId) return null;
    const location = await setDefaultHospitalLocation(user.tenantId);
    if (location) {
      setHospitalDefaultLocation(location);
    }
    return location;
  };

  const handleBookingTypeChange = async (newType: 'to_hospital' | 'from_hospital') => {
    const hospitalLocation = await ensureHospitalDefaultLocation();

    if (editingBooking) {
      setFormData((prev) => ({ ...prev, bookingType: newType }));
      return;
    }

    setFormData((prev) => {
      const updated = { ...prev, bookingType: newType };

      if (newType === 'to_hospital') {
        // Default drop to hospital; allow override later
        if (hospitalLocation) {
          updated.dropoffLocation = hospitalLocation;
        }
      }

      if (newType === 'from_hospital') {
        // Default pickup from hospital; clear drop so user chooses destination
        if (hospitalLocation) {
          updated.pickupLocation = hospitalLocation;
        }
        const patient = patients.find((p) => p._id === prev.patientId);
        const patientLoc = patient?.spec?.patientLocation;
        const hasPatientLoc =
          patientLoc &&
          typeof patientLoc.latitude === 'number' &&
          typeof patientLoc.longitude === 'number' &&
          !isNaN(patientLoc.latitude) &&
          !isNaN(patientLoc.longitude);
        updated.dropoffLocation = hasPatientLoc
          ? {
            latitude: patientLoc.latitude,
            longitude: patientLoc.longitude,
            address: (patient as any)?.spec?.address || (patient as any)?.metadata?.address || undefined,
          }
          : null;
      }

      return updated;
    });
  };

  // Handle patient selection: default pickup from patientLocation, dropoff from tenant hospital
  const handlePatientChange = async (patientId: string) => {
    const isCreatingNew = !editingBooking;

    let nextPickup = formData.pickupLocation;
    let nextDropoff = formData.dropoffLocation;

    if (patientId && isCreatingNew) {
      const selectedPatient = patients.find((p) => p._id === patientId);
      const patientTenantId =
        selectedPatient?.metadata?.tenantId ?? (selectedPatient as any)?.spec?.tenantId ?? null;

      // Default pickup from patient's stored location
      if (
        selectedPatient?.spec?.patientLocation?.latitude !== undefined &&
        selectedPatient.spec.patientLocation.longitude !== undefined
      ) {
        nextPickup = {
          latitude: selectedPatient.spec.patientLocation.latitude,
          longitude: selectedPatient.spec.patientLocation.longitude,
          address: selectedPatient.spec.address || undefined,
        };
      }

      // Default dropoff from tenant hospital
      if (patientTenantId) {
        let tenant = tenants.find((t) => t._id === patientTenantId);
        if (!tenant) {
          try {
            const resp = await getTenant(patientTenantId);
            if (resp.data.success) {
              tenant = resp.data.data;
            }
          } catch {
            // ignore fetch failure
          }
        }

        const hospital = tenant?.spec?.hospital;
        const hospitalLat = hospital?.hospitalLocation?.latitude ?? hospital?.latitude;
        const hospitalLng = hospital?.hospitalLocation?.longitude ?? hospital?.longitude;
        if (hospitalLat !== undefined && hospitalLng !== undefined) {
          const addressParts = [
            hospital?.name,
            hospital?.address,
            hospital?.city,
            hospital?.state,
            hospital?.zipCode,
            hospital?.country,
          ].filter(Boolean);
          nextDropoff = {
            latitude: hospitalLat,
            longitude: hospitalLng,
            address: addressParts.join(', ') || undefined,
          };
        } else {
          const addressParts = [
            hospital?.name,
            hospital?.address,
            hospital?.city,
            hospital?.state,
            hospital?.zipCode,
            hospital?.country,
          ].filter(Boolean);
          if (addressParts.length > 0) {
            const hospitalAddress = addressParts.join(', ');
            const location = await geocodeHospitalAddress(hospitalAddress);
            if (location) {
              nextDropoff = location;
            }
          }
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      patientId,
      pickupLocation: nextPickup,
      dropoffLocation: patientId ? nextDropoff : isCreatingNew ? null : prev.dropoffLocation,
    }));
  };

  // Capitalize the first letter of each word in a name
  const capitalizeName = (value: string): string => {
    if (!value || typeof value !== 'string') return value;
    return value
      .trim()
      .split(/\s+/)
      .map((word) => {
        if (!word) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  // Validate phone number
  const validatePhone = (phone: string): string => {
    if (!phone || !phone.trim()) {
      return 'Phone number is required';
    }
    // Remove spaces, dashes, and parentheses for validation
    const cleanedPhone = phone.replace(/[\s\-()]/g, '');
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(cleanedPhone)) {
      return 'Phone number must be exactly 10 digits';
    }
    return '';
  };

  // Format patient age - show days if less than 1 year
  const formatPatientAge = (patient: Patient): string => {
    if (!patient) return 'N/A';

    const age = patient.spec?.age;
    if (age === undefined || age === null) return 'N/A';

    // If age is 0 or less than 1 year, calculate days from dateOfBirth
    if (age === 0 || age < 1) {
      const dateOfBirth = patient.spec?.dateOfBirth;
      if (dateOfBirth) {
        const dob = new Date(dateOfBirth);
        const today = new Date();
        const diffTime = today.getTime() - dob.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return 'N/A'; // Future date
        if (diffDays === 0) return '0 days';
        if (diffDays === 1) return '1 day';
        return `${diffDays} days`;
      }
    }

    // For age >= 1, show years
    if (age === 1) return '1 year';
    return `${age} years`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (createBookingSubmitting) return;
    setFormError('');
    setFieldErrors({});
    setCreateBookingSubmitting(true);

    // If creating new patient, validate patient fields
    if (formData.createNewPatient) {
      const newPatientFieldErrors: {
        newPatientName?: string;
        newPatientPhone?: string;
        newPatientGender?: string;
        newPatientDob?: string;
        newPatientContactPersonName?: string;
        newPatientContactPersonPhone?: string;
      } = {};

      // Gender is optional

      // Contact person phone number is mandatory
      const hasContactPersonPhone = formData.patientContactPersonPhone && formData.patientContactPersonPhone.trim();

      if (!hasContactPersonPhone) {
        newPatientFieldErrors.newPatientContactPersonPhone = 'Contact person phone number is required';
      } else {
        // Validate contact person phone format
        const contactPersonPhoneError = validatePhone(formData.patientContactPersonPhone);
        if (contactPersonPhoneError) {
          newPatientFieldErrors.newPatientContactPersonPhone = contactPersonPhoneError;
        }
      }

      // Validate phone if provided (optional field)
      if (formData.patientPhone && formData.patientPhone.trim()) {
        const phoneError = validatePhone(formData.patientPhone);
        if (phoneError) {
          setPatientPhoneError(phoneError);
          setFieldErrors((prev) => ({
            ...prev,
            newPatientPhone: phoneError,
          }));
          setFormError('Please fix the highlighted errors.');
          setCreateBookingSubmitting(false);
          return;
        }
      }
      setPatientPhoneError('');

      if (Object.keys(newPatientFieldErrors).length > 0) {
        setFieldErrors((prev) => ({ ...prev, ...newPatientFieldErrors }));
        setFormError('Please fix the highlighted errors.');
        setCreateBookingSubmitting(false);
        return;
      }
    }

    // Validate core booking fields (patient / locations / time)
    const newFieldErrors: {
      patientId?: string;
      pickupLocation?: string;
      dropoffLocation?: string;
      scheduledPickupTime?: string;
      newPatientName?: string;
      newPatientPhone?: string;
      newPatientGender?: string;
      newPatientDob?: string;
      newPatientEmergencyContactName?: string;
      newPatientEmergencyContactPhone?: string;
    } = {};

    if (!formData.createNewPatient && !formData.patientId) {
      newFieldErrors.patientId = 'Patient is required';
    }

    if (!formData.pickupLocation) {
      newFieldErrors.pickupLocation = formData.createNewPatient
        ? 'Patient location (pickup location) is required'
        : 'Pickup location is required';
    }

    if (!formData.dropoffLocation) {
      newFieldErrors.dropoffLocation = 'Dropoff location is required';
    }

    if (!formData.scheduledPickupTime) {
      newFieldErrors.scheduledPickupTime = 'Scheduled pickup time is required';
    }

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setFormError('Please fix the highlighted errors.');
      setCreateBookingSubmitting(false);
      return;
    }

    try {
      let finalPatientId = formData.patientId;
      let patientTenantId = null;

      // Create patient first if creating new patient
      if (formData.createNewPatient) {
        // Calculate age from date of birth if provided
        let age: number | undefined = undefined;
        let dateOfBirth: number | undefined = undefined;
        if (formData.patientDateOfBirth) {
          const dob = new Date(formData.patientDateOfBirth);
          const today = new Date();
          age = today.getFullYear() - dob.getFullYear();
          const monthDiff = today.getMonth() - dob.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
            age--;
          }
          dateOfBirth = dob.getTime();
        }

        const patientData = {
          metadata: {
            name: formData.patientName.trim() ? capitalizeName(formData.patientName.trim()) : 'Unknown',
          },
          spec: {
            age: age, // Age is optional, undefined if dateOfBirth not provided
            gender: formData.patientGender,
            phone: formData.patientPhone.trim() || undefined,
            dateOfBirth: dateOfBirth,
            idNumber: formData.patientIdNumber.trim() || undefined,
            contactPerson: {
              name: formData.patientContactPersonName.trim() ? capitalizeName(formData.patientContactPersonName.trim()) : undefined,
              phone: formData.patientContactPersonPhone.trim() || undefined,
            },
            patientLocation: formData.pickupLocation
              ? {
                latitude: formData.pickupLocation.latitude,
                longitude: formData.pickupLocation.longitude,
              }
              : undefined,
            address: formData.pickupLocation?.address || undefined,
            vitalInfo: Object.keys(formData.vitalInfo).length > 0 ? formData.vitalInfo : undefined,
          },
        };

        try {
          const patientResponse = await createPatient(patientData);
          finalPatientId = patientResponse.data.data._id;
          patientTenantId = patientResponse.data.data.metadata?.tenantId || null;

          // Refresh patients list
          await fetchPatients();
        } catch (patientErr: any) {
          // If patient creation fails, show error and don't close modal
          setFormError(patientErr.response?.data?.message || 'Failed to create patient. Please try again.');
          return; // Don't proceed with booking creation
        }
      } else {
        // Get existing patient info
        const selectedPatient = patients.find((p) => p._id === formData.patientId);
        patientTenantId =
          selectedPatient?.metadata?.tenantId ??
          (selectedPatient as any)?.spec?.tenantId ??
          null;
      }

      const tenantForCode = tenants.find((t) => t._id === (user?.tenantId || patientTenantId));
      const tCode = tenantForCode?.metadata?.code?.toUpperCase() || 'BKG';
      const nowDate = new Date();
      const yyyymm = `${nowDate.getFullYear()}${String(nowDate.getMonth() + 1).padStart(2, '0')}`;
      const epochSec = Math.floor(Date.now() / 1000);
      const bookingName = formData.name || `${tCode}-${yyyymm}-${epochSec}`;

      // Reverse geocode pickup location
      const pickupReverseGeocoded = await reverseGeocode(
        formData.pickupLocation!.latitude,
        formData.pickupLocation!.longitude
      );

      // Combine address details + reverse geocoded address for pickup
      const pickupAddressDetails = formData.pickupAddressDetails.trim();
      const combinedPickupAddress = pickupAddressDetails
        ? `${pickupAddressDetails}${pickupReverseGeocoded ? ', ' + pickupReverseGeocoded : ''}`
        : pickupReverseGeocoded || '';

      // Reverse geocode dropoff location
      const dropoffReverseGeocoded = await reverseGeocode(
        formData.dropoffLocation!.latitude,
        formData.dropoffLocation!.longitude
      );

      // Combine address details + reverse geocoded address for dropoff
      const dropoffAddressDetails = formData.dropoffAddressDetails.trim();
      const combinedDropoffAddress = dropoffAddressDetails
        ? `${dropoffAddressDetails}${dropoffReverseGeocoded ? ', ' + dropoffReverseGeocoded : ''}`
        : dropoffReverseGeocoded || '';

      const data: any = {
        metadata: {
          name: (bookingName || '').trim().toUpperCase(),
          tenantId: user?.tenantId || patientTenantId,
        },
        spec: {
          patientId: finalPatientId,
          priority: formData.priority,
          bookingType: formData.bookingType,
          medicalCondition: formData.medicalCondition.trim() || undefined,
          notes: formData.notes.trim() || undefined,
          bookingAmount: formData.bookingAmount.trim() || undefined,
          pickupLocation: {
            ...formData.pickupLocation!,
            address: combinedPickupAddress,
          },
          dropoffLocation: {
            ...formData.dropoffLocation!,
            address: combinedDropoffAddress,
          },
          timings: {
            bookingTime: Date.now(),
            scheduledPickupTime: new Date(formData.scheduledPickupTime).getTime(),
          },
        },
      };

      if (editingBooking) {
        await updateBooking(editingBooking._id, data);
      } else {
        await createBooking(data);
      }

      setShowModal(false);
      fetchBookings();
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Operation failed');
    } finally {
      setCreateBookingSubmitting(false);
    }
  };

  const handleAssignSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (assignSubmitting) return;
    setAssignError('');
    setAssignSubmitting(true);

    if (!assigningBooking) {
      setAssignSubmitting(false);
      return;
    }

    // Validate that ambulance and pilot are selected (staff is optional)
    const hasAmbulance = !!assignFormData.ambulanceId;
    const hasPilot = !!assignFormData.pilotId;

    if (!hasAmbulance || !hasPilot) {
      const missingFields = [];
      if (!hasAmbulance) missingFields.push('Ambulance');
      if (!hasPilot) missingFields.push('Pilot/Driver');
      setAssignError(`Please select ${missingFields.join(', ')}`);
      setAssignSubmitting(false);
      return;
    }

    try {
      // Backend will handle:
      // 1. Checking if pilot/staff is locked (in_transit/arrived) - will return error if locked
      // 2. Automatically removing pilot/staff from other pending/assigned bookings

      // Determine if we should update status to 'assigned'
      // Status should change to 'assigned' if:
      // - Current status is 'pending'
      // - Ambulance and pilot are assigned (staff is optional)
      const shouldUpdateToAssigned =
        assigningBooking.spec.status === 'pending' &&
        hasAmbulance &&
        hasPilot;

      const data: any = {
        spec: {
          ambulanceId: assignFormData.ambulanceId || undefined,
          pilotId: assignFormData.pilotId || undefined,
          assignedStaffIds: assignFormData.assignedStaffIds,
          bookingType: assignFormData.bookingType,
          bookingAmount: assignFormData.bookingAmount?.trim() || undefined,
          ...(shouldUpdateToAssigned && { status: 'assigned' }),
        },
      };

      await updateBooking(assigningBooking._id, data);

      handleCloseAssignModal();
      fetchBookings();
    } catch (err: any) {
      setAssignError(err.response?.data?.message || 'Operation failed');
    } finally {
      setAssignSubmitting(false);
    }
  };

  const handleStatusUpdate = async (bookingId: string, newStatus: string) => {
    // Show confirmation for cancellation
    if (newStatus === 'cancelled') {
      setBookingToCancel(bookingId);
      setShowCancelBookingConfirm(true);
      return;
    }

    try {

      await updateBooking(bookingId, { spec: { status: newStatus } });

      fetchBookings();
    } catch (err: any) {
      setListError(err.response?.data?.message || 'Failed to update status');
    }
  };

  const confirmCancelBooking = async (cancellationReason: string) => {
    if (!bookingToCancel) return;
    try {
      await updateBooking(bookingToCancel, {
        spec: { status: 'cancelled', cancellationReason: cancellationReason || undefined },
      });
      setShowCancelBookingConfirm(false);
      setBookingToCancel(null);
      fetchBookings();
    } catch (err: any) {
      setListError(err.response?.data?.message || 'Failed to cancel booking');
      throw err;
    }
  };

  const handleDelete = (booking: Booking) => {
    setBookingToDelete({ id: booking._id, name: booking.metadata.name });
    setShowDeleteConfirm(true);
  };

  const handleCancelPilotAssignment = (bookingId: string) => {
    setPilotAssignmentToCancel(bookingId);
    setShowCancelPilotAssignmentConfirm(true);
  };

  const confirmCancelPilotAssignment = async (cancellationReason: string) => {
    if (!pilotAssignmentToCancel) return;

    try {
      await cancelPilotAssignment(pilotAssignmentToCancel, cancellationReason);
      setShowCancelPilotAssignmentConfirm(false);
      setPilotAssignmentToCancel(null);
      fetchBookings();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to cancel pilot assignment';
      setListError(errorMessage);
      setShowCancelPilotAssignmentConfirm(false);
      setPilotAssignmentToCancel(null);
    }
  };

  const confirmDelete = async () => {
    if (!bookingToDelete) return;

    try {
      await deleteBooking(bookingToDelete.id);
      setShowDeleteConfirm(false);
      setBookingToDelete(null);
      fetchBookings();
    } catch (err: any) {
      setListError(err.response?.data?.message || 'Failed to delete booking');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#ff9800';
      case 'assigned':
        return '#2196f3';
      case 'in_transit':
        return '#9c27b0';
      case 'patient_picked':
        return '#00bcd4';
      case 'patient_dropped':
        return '#795548';
      case 'completed':
        return '#4caf50';
      case 'cancelled':
        return '#fa003f';
      default:
        return '#999';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return '#f44336';
      case 'high':
        return '#ff9800';
      case 'medium':
        return '#2196f3';
      case 'low':
        return '#4caf50';
      default:
        return '#999';
    }
  };

  const getLastStatusUpdateTime = (booking: Booking): number | null => {
    const timings = booking.spec.timings;
    if (!timings) return null;

    const patientPickedTime = (timings as { patientPickedTime?: number }).patientPickedTime ?? timings.arrivedTime;
    const statusUpdateTimes = [
      timings.pendingTime,
      timings.assignedTime,
      timings.inTransitTime,
      patientPickedTime,
      (timings as { patientDroppedTime?: number }).patientDroppedTime,
      timings.cancelledTime,
      timings.completionTime,
    ].filter((time): time is number => typeof time === 'number' && time > 0);

    return statusUpdateTimes.length > 0 ? Math.max(...statusUpdateTimes) : null;
  };

  // Helper function to truncate address to short form
  const truncateAddress = (address: string, maxLength: number = 40): string => {
    if (!address) return '';
    if (address.length <= maxLength) return address;
    return address.substring(0, maxLength) + '...';
  };

  // Helper function to format location for display
  const formatLocation = (location: { latitude: number; longitude: number; address?: string } | null | undefined): { short: string; full: string | null; hasAddress: boolean } => {
    if (!location) {
      return { short: '—', full: null, hasAddress: false };
    }

    const coords = `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`;

    // If there's an address, use it; otherwise use coordinates
    if (location.address) {
      return {
        short: truncateAddress(location.address, 40),
        full: location.address,
        hasAddress: true,
      };
    } else {
      return {
        short: coords,
        full: null,
        hasAddress: false,
      };
    }
  };

  return (
    <DashboardLayout>
      {/* <div className="page-header">
        <h2>Booking Management</h2>
        <p>Manage ambulance bookings and assignments</p>
      </div> */}

      {listError && (
        <div className="error-message" style={{ marginBottom: '20px', padding: '12px', background: '#fee', color: '#c33', borderRadius: '4px' }}>
          {listError}
        </div>
      )}

      <div className="card" style={{ marginTop: '24px', height: 'calc(100vh - 185px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className="card-header" style={{ flexShrink: 0 }}>
          {/* <h3 className="card-title">Bookings</h3> */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
            <div className="booking-filters-row" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <input
                type="text"
                placeholder="Search by reference, patient, ambulance, pilot, or booked by..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input booking-search"
                style={{
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '16px',
                  width: '220px',
                  flex: '0 0 220px',
                  border: '1px solid',
                  borderColor: isDarkTheme ? '#334155' : '#ddd',
                  height: '36px',
                  boxSizing: 'border-box',
                }}
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ padding: '8px', border: '1px solid', borderColor: isDarkTheme ? '#334155' : '#ddd', borderRadius: '4px', height: '36px', boxSizing: 'border-box', width: '105px' }}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="assigned">Assigned</option>
                <option value="in_transit">In Transit</option>
                <option value="patient_picked">Patient Picked</option>
                <option value="patient_dropped">Patient Dropped</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                style={{ padding: '8px', border: '1px solid', borderColor: isDarkTheme ? '#334155' : '#ddd', borderRadius: '4px', height: '36px', boxSizing: 'border-box', width: '125px' }}
              >
                <option value="">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
              <select
                value={bookingTypeFilter}
                onChange={(e) => setBookingTypeFilter(e.target.value)}
                style={{ padding: '8px', border: '1px solid', borderColor: isDarkTheme ? '#334155' : '#ddd', borderRadius: '4px', height: '36px', boxSizing: 'border-box', width: '150px' }}
              >
                <option value="">All Booking Types</option>
                <option value="to_hospital">{BOOKING_TYPE_LABELS.to_hospital}</option>
                <option value="from_hospital">{BOOKING_TYPE_LABELS.from_hospital}</option>
              </select>
              <select
                value={dateFilterType}
                onChange={(e) => setDateFilterType(e.target.value as DateFilterType)}
                style={{ padding: '8px', border: '1px solid', borderColor: isDarkTheme ? '#334155' : '#ddd', borderRadius: '4px', height: '36px', boxSizing: 'border-box', width: '135px' }}
              >
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="thisWeek">This Week</option>
                <option value="lastWeek">Last Week</option>
                <option value="thisMonth">This Month</option>
                <option value="lastMonth">Last Month</option>
                <option value="tomorrow">Tomorrow</option>
                <option value="next7days">Next 7 Days</option>
                <option value="custom">Custom Dates</option>
              </select>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="refresh-button"
                title="Refresh list"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"></polyline>
                  <polyline points="1 20 1 14 7 14"></polyline>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                </svg>
                {loading ? 'Loading...' : 'Refresh'}
              </button>
              <div style={{ position: 'relative' }} ref={downloadMenuRef}>
                <button
                  onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                  disabled={downloading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: downloading ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: '500',
                    opacity: downloading ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {downloading ? 'Generating...' : 'Download'}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ marginLeft: '6px' }}
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </button>
                {showDownloadMenu && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '5px',
                      backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
                      border: isDarkTheme ? '1px solid #334155' : '1px solid #ddd',
                      borderRadius: '6px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      zIndex: 1000,
                      minWidth: '150px',
                    }}
                  >
                    <button
                      onClick={() => handleDownload('pdf')}
                      disabled={downloading}
                      style={{
                        width: '100%',
                        padding: '10px 15px',
                        backgroundColor: 'transparent',
                        color: isDarkTheme ? '#ffffff' : '#333',
                        border: 'none',
                        borderRadius: '6px 6px 0 0',
                        cursor: downloading ? 'not-allowed' : 'pointer',
                        fontSize: '16px',
                        textAlign: 'left',
                        opacity: downloading ? 0.6 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (!downloading) {
                          e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f5f5f5';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      Download as PDF
                    </button>
                    <button
                      onClick={() => handleDownload('excel')}
                      disabled={downloading}
                      style={{
                        width: '100%',
                        padding: '10px 15px',
                        backgroundColor: 'transparent',
                        color: isDarkTheme ? '#ffffff' : '#333',
                        border: 'none',
                        borderTop: isDarkTheme ? '1px solid #334155' : '1px solid #ddd',
                        borderRadius: '0 0 6px 6px',
                        cursor: downloading ? 'not-allowed' : 'pointer',
                        fontSize: '16px',
                        textAlign: 'left',
                        opacity: downloading ? 0.6 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (!downloading) {
                          e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f5f5f5';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      Download as Excel
                    </button>
                  </div>
                )}
              </div>
              {hasPermission('booking.create') && (
                <button
                  className="btn-add"
                  onClick={() => handleOpenModal()}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  + New Booking
                </button>
              )}
            </div>
            {downloadError && (
              <div style={{
                padding: '10px',
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: '4px',
                color: '#c33',
                fontSize: '16px',
                marginTop: '10px',
                marginBottom: '10px'
              }}>
                {downloadError}
              </div>
            )}
            {dateFilterType === 'custom' && (
              <div className="booking-dates-row" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <label style={{
                  fontSize: '16px',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  color: isDarkTheme ? '#f1f5f9' : '#1e293b'
                }}>Start Date:</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCustomStartDate(value);
                    if (value && customEndDate) {
                      const start = new Date(value);
                      const end = new Date(customEndDate);
                      const diffDays = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
                      if (diffDays > 31) setCustomEndDate(getMaxEndDateForStart(value));
                    }
                  }}
                  max={getMaxDateFuture()}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '16px',
                    border: isDarkTheme ? '1px solid #334155' : '1px solid #ddd',
                    backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
                    color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                    height: '36px',
                    boxSizing: 'border-box',
                    width: '180px',
                  }}
                />
                <label style={{
                  fontSize: '16px',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  color: isDarkTheme ? '#f1f5f9' : '#1e293b'
                }}>End Date:</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  min={customStartDate || undefined}
                  max={customStartDate ? getMaxEndDateForStart(customStartDate) : getMaxDateFuture()}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '16px',
                    border: isDarkTheme ? '1px solid #334155' : '1px solid #ddd',
                    backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
                    color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                    height: '36px',
                    boxSizing: 'border-box',
                    width: '180px',
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {loading ? (
            <div className="loading">Loading bookings...</div>
          ) : bookings.length === 0 ? (
            <div className="empty-state">
              <h3>No bookings yet</h3>
              {/* <p>Create a new booking to get started</p> */}
            </div>
          ) : (
            <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <div className="table-container" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <table className="bookings-table">
                  <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <tr>
                      <th style={{ whiteSpace: 'nowrap' }}>Reference</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Priority</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Booking Type</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Status</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Amount</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Pilot</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Patient</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Contact Person</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Ambulance</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Booked By</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Pickup Location</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Dropoff Location</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Booking Time</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Last Update</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => {
                      // Use populated data from booking API instead of separate lookups
                      const patientName = booking.spec.patientInfo?.name || booking.spec.patientId || '—';
                      const patientNumber = booking.spec.patientInfo?.number || '—';
                      const contactPersonName = booking.spec.patientInfo?.contactPersonName || '—';
                      const contactPersonNumber = booking.spec.patientInfo?.contactPersonNumber || '—';
                      const ambulanceName = booking.spec.ambulanceInfo
                        ? `${booking.spec.ambulanceInfo.name || ''} (${booking.spec.ambulanceInfo.licensePlate || ''})`.trim()
                        : '—';
                      const pilotName = booking.spec.pilotInfo?.name || '—';
                      const pilotNumber = booking.spec.pilotInfo?.number || '—';

                      return (
                        <tr
                          key={booking._id}
                          onClick={(e) => {
                            // Don't open drawer if clicking on action buttons
                            const target = e.target as HTMLElement;
                            if (target.closest('.action-buttons') || target.closest('button')) {
                              return;
                            }
                            setSelectedBooking(booking);
                            setShowDetailsDrawer(true);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <td style={{ whiteSpace: 'nowrap' }}>{(booking.metadata?.name ?? '').toUpperCase()}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <span
                              style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                color: 'white',
                                background: getPriorityColor(booking.spec.priority),
                              }}
                            >
                              {booking.spec.priority.toUpperCase()}
                            </span>
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <span
                              style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                color: 'white',
                                background: booking.spec.bookingType ? BOOKING_TYPE_COLORS[booking.spec.bookingType] : '#e2e8f0',
                              }}
                            >
                              {booking.spec.bookingType ? BOOKING_TYPE_LABELS[booking.spec.bookingType] : '—'}
                            </span>
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <span
                              style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                color: 'white',
                                background: getStatusColor(booking.spec.status),
                              }}
                            >
                              {booking.spec.status.replace('_', ' ').toUpperCase()}
                            </span>
                          </td>
                          <td style={{ whiteSpace: 'nowrap', textAlign: 'right', fontWeight: 600 }}>
                            {booking.spec.bookingAmount ? `₹${Number(booking.spec.bookingAmount).toLocaleString()}` : '—'}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <div
                              className="action-buttons"
                              style={{
                                display: 'flex',
                                flexWrap: 'nowrap',
                                gap: '6px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {hasPermission('booking.update') && booking.spec.status === 'pending' && (
                                <button
                                  className="btn-icon btn-edit"
                                  onClick={() => handleOpenAssignModal(booking, 'assign')}
                                  title="Assign ambulance and staff"
                                >
                                  Assign
                                </button>
                              )}
                              {hasPermission('booking.update') && booking.spec.status === 'assigned' && (
                                <>
                                  <button
                                    className="btn-icon"
                                    onClick={() => handleOpenAssignModal(booking, 'reassign')}
                                    title="Reassign ambulance and staff"
                                    style={{ background: '#f97316', color: 'white' }}
                                  >
                                    Reassign
                                  </button>
                                  <button
                                    className="btn-icon"
                                    onClick={() => handleStatusUpdate(booking._id, 'in_transit')}
                                    title="Start trip"
                                    style={{ background: '#9c27b0', color: 'white' }}
                                  >
                                    Start Trip
                                  </button>
                                </>
                              )}
                              {hasPermission('booking.update') && booking.spec.status === 'in_transit' && (
                                <button
                                  className="btn-icon"
                                  onClick={() => handleStatusUpdate(booking._id, 'patient_picked')}
                                  title="Mark patient picked up"
                                  style={{ background: '#00bcd4', color: 'white' }}
                                >
                                  Patient Pickedup
                                </button>
                              )}
                              {hasPermission('booking.update') && booking.spec.status === 'patient_picked' && booking.spec.bookingType === 'from_hospital' && (
                                <button
                                  className="btn-icon"
                                  onClick={() => handleStatusUpdate(booking._id, 'patient_dropped')}
                                  title="Mark patient dropped"
                                  style={{ background: '#795548', color: 'white' }}
                                >
                                  Patient Dropped
                                </button>
                              )}
                              {hasPermission('booking.update') && ((booking.spec.status === 'patient_picked' && booking.spec.bookingType === 'to_hospital') || booking.spec.status === 'patient_dropped') && (
                                <button
                                  className="btn-icon"
                                  onClick={() => handleStatusUpdate(booking._id, 'completed')}
                                  title="Complete booking"
                                  style={{ background: '#4caf50', color: 'white' }}
                                >
                                  Complete
                                </button>
                              )}
                              {/* Pilot can cancel their assignment if they are assigned and status is pending/assigned */}
                              {booking.spec.pilotId === (user?._id || user?.id) &&
                                (booking.spec.status === 'pending' || booking.spec.status === 'assigned') && (
                                  <button
                                    className="btn-icon"
                                    onClick={() => handleCancelPilotAssignment(booking._id)}
                                    title="Cancel my assignment"
                                    style={{ background: '#ff9800', color: 'white' }}
                                  >
                                    Cancel Assignment
                                  </button>
                                )}
                              {/* Only tenant admin can cancel the whole booking */}
                              {hasPermission('booking.update') &&
                                (isTenantAdmin || isSystemAdmin) &&
                                (booking.spec.status === 'pending' || booking.spec.status === 'assigned') && (
                                  <button
                                    className="btn-icon"
                                    onClick={() => handleStatusUpdate(booking._id, 'cancelled')}
                                    title="Cancel booking"
                                    style={{ background: '#fa003f', color: 'white' }}
                                  >
                                    Cancel
                                  </button>
                                )}
                              {hasPermission('booking.delete') && booking.spec.status === 'cancelled' && (
                                <button
                                  className="btn-icon btn-delete"
                                  onClick={() => handleDelete(booking)}
                                  title="Delete booking"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                          <td>
                            {pilotName !== '—' || pilotNumber !== '—' ? (
                              <div>
                                <div>{pilotName}</div>
                                {pilotNumber !== '—' && <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>{pilotNumber}</div>}
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            {patientName !== '—' || patientNumber !== '—' ? (
                              <div>
                                <div>{patientName}</div>
                                {patientNumber !== '—' && <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>{patientNumber}</div>}
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            {contactPersonName !== '—' || contactPersonNumber !== '—' ? (
                              <div>
                                {contactPersonName !== '—' && <div>{contactPersonName}</div>}
                                {contactPersonNumber !== '—' && <div style={{ fontSize: '14px', color: '#666', marginTop: contactPersonName !== '—' ? '2px' : '0' }}>{contactPersonNumber}</div>}
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>{ambulanceName}</td>
                          <td>{getBookedByName(booking.spec.bookedBy, booking.spec.bookedByDisplay)}</td>
                          <td
                            style={{
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {(() => {
                              const pickupLoc = formatLocation(booking.spec.pickupLocation);
                              return pickupLoc.hasAddress ? (
                                <span
                                  title={pickupLoc.full || ''}
                                  style={{
                                    cursor: 'help',
                                    display: 'inline-block',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {pickupLoc.short}
                                </span>
                              ) : (
                                <span>{pickupLoc.short}</span>
                              );
                            })()}
                          </td>
                          <td
                            style={{
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {(() => {
                              const dropoffLoc = formatLocation(booking.spec.dropoffLocation);
                              return dropoffLoc.hasAddress ? (
                                <span
                                  title={dropoffLoc.full || ''}
                                  style={{
                                    cursor: 'help',
                                    display: 'inline-block',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {dropoffLoc.short}
                                </span>
                              ) : (
                                <span>{dropoffLoc.short}</span>
                              );
                            })()}
                          </td>
                          <td>{booking.spec.timings?.bookingTime ? formatDateTimeDDMMYYYY(booking.spec.timings.bookingTime) : (booking.metadata.createdAt ? formatDateTimeDDMMYYYY(booking.metadata.createdAt) : '—')}</td>
                          <td>{(() => {
                            const lastStatusUpdate = getLastStatusUpdateTime(booking);
                            return lastStatusUpdate ? formatDateTimeDDMMYYYY(lastStatusUpdate) : (booking.metadata.updatedAt ? formatDateTimeDDMMYYYY(booking.metadata.updatedAt) : '—');
                          })()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="pagination-controls" style={{ flexShrink: 0, position: 'relative', width: '100%' }}>
                <div className="pagination-left" style={{ position: 'absolute', right: 250, whiteSpace: 'nowrap', zIndex: 10 }}>
                  <label style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>
                    Rows per page:{' '}
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      style={{ minWidth: '60px', width: 'auto' }}
                    >
                      {[10, 20, 50, 100].map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="pagination-center">
                  <span className="pagination-info">
                    {totalItems === 0
                      ? '0 of 0'
                      : `${(currentPage - 1) * pageSize + 1}–${Math.min(
                        currentPage * pageSize,
                        totalItems
                      )} of ${totalItems}`}
                  </span>
                </div>
                <div className="pagination-right">
                  <button
                    className="btn-secondary"
                    disabled={!hasPreviousPage || loading}
                    onClick={() => hasPreviousPage && handlePageChange(currentPage - 1)}
                  >
                    ‹
                  </button>
                  <button
                    className="btn-secondary"
                    disabled={!hasNextPage || loading}
                    onClick={() => hasNextPage && handlePageChange(currentPage + 1)}
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Booking Drawer */}
      {showModal && (
        <div className="drawer-overlay" onClick={() => setShowModal(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">{editingBooking ? 'Edit Booking' : 'Create New Booking'}</h3>
              <button className="drawer-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="drawer-form">
              <div className="drawer-body">
                {formError && <div className="error-message">{formError}</div>}

                {editingBooking && (
                  <div className="form-group">
                    <label>Booking Reference</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Auto-generated if left empty"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>
                    Patient
                    <span className="required-star">*</span>
                  </label>
                  <div ref={patientDropdownRef} style={{ position: 'relative', width: '100%', zIndex: showPatientDropdown ? 10001 : 'auto' }}>
                    <button
                      type="button"
                      className={`custom-select-button ${fieldErrors.patientId ? 'input-error' : ''}`}
                      onClick={() => {
                        setShowPatientDropdown(!showPatientDropdown);
                        if (!showPatientDropdown) {
                          setPatientSearchTerm('');
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid',
                        borderRadius: '6px',
                        textAlign: 'left',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <span>
                        {(() => {
                          const selectedPatient = patients.find((p) => p._id === formData.patientId);
                          return selectedPatient
                            ? `${selectedPatient.metadata?.name} (Age: ${formatPatientAge(selectedPatient)})`
                            : 'Select a patient';
                        })()}
                      </span>
                      <span style={{ fontSize: '14px' }}>{showPatientDropdown ? '▲' : '▼'}</span>
                    </button>
                    {showPatientDropdown && (
                      <div
                        className="custom-dropdown-menu"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          marginTop: '4px',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          zIndex: 10000,
                          maxHeight: '300px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          isolation: 'isolate',
                        }}
                      >
                        <div style={{ display: 'flex', borderBottom: '1px solid', alignItems: 'center' }}>
                          <input
                            type="text"
                            placeholder="Search patient by name or phone number..."
                            value={patientSearchTerm}
                            onChange={(e) => setPatientSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="search-input"
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              border: 'none',
                              borderRadius: '6px 6px 0 0',
                              fontSize: '16px',
                              outline: 'none',
                            }}
                            autoFocus
                          />
                          {hasPermission('patient.create') && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowPatientDropdown(false);
                                setFormData({ ...formData, createNewPatient: true, patientId: '' });
                              }}
                              style={{
                                padding: '8px 12px',
                                border: 'none',
                                borderLeft: '1px solid',
                                borderRadius: '0',
                                fontSize: '16px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                fontWeight: '500',
                                transition: 'background-color 0.2s',
                              }}
                              className="add-patient-button"
                            >
                              + Add New Patient
                            </button>
                          )}
                        </div>
                        <div style={{ maxHeight: '250px', overflowY: 'auto', overflowX: 'hidden' }}>
                          {(() => {
                            const searchLower = patientSearchTerm.toLowerCase().trim();

                            // Get patient IDs that have active bookings (not completed or cancelled)
                            const activeBookingStatuses = ['pending', 'assigned', 'in_transit', 'patient_picked', 'patient_dropped'];
                            const patientsWithActiveBookings = new Set(
                              bookings
                                .filter((booking) =>
                                  booking.spec.patientId &&
                                  activeBookingStatuses.includes(booking.spec.status)
                                )
                                .map((booking) => booking.spec.patientId)
                            );

                            const filteredPatients = patients.filter((patient) => {
                              // Exclude patients with active bookings
                              if (patientsWithActiveBookings.has(patient._id)) {
                                return false;
                              }

                              // Filter by search term
                              if (searchLower !== '') {
                                const name = (patient.metadata?.name || '').toLowerCase();
                                const phone = (patient.spec?.phone || '').toLowerCase();
                                if (!name.includes(searchLower) && !phone.includes(searchLower)) {
                                  return false;
                                }
                              }
                              return true;
                            });

                            if (filteredPatients.length === 0) {
                              return (
                                <div
                                  style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    fontSize: '16px',
                                  }}
                                  className="empty-text"
                                >
                                  {patientSearchTerm ? `No patients found matching "${patientSearchTerm}"` : 'No patients available'}
                                </div>
                              );
                            }

                            return filteredPatients.map((patient) => {
                              const handleSelect = () => {
                                handlePatientChange(patient._id);
                                setFormData(prev => ({ ...prev, createNewPatient: false, patientName: '', patientPhone: '', patientGender: '', patientDateOfBirth: '', patientContactPersonName: '', patientContactPersonPhone: '', vitalInfo: {} }));
                                setShowPatientDropdown(false);
                                setPatientSearchTerm('');
                              };

                              return (
                                <div
                                  key={patient._id}
                                  onClick={handleSelect}
                                  className={`custom-dropdown-option ${formData.patientId === patient._id ? 'selected' : ''}`}
                                  onMouseEnter={(e) => {
                                    if (formData.patientId !== patient._id) {
                                      e.currentTarget.style.backgroundColor = '';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    const isSelected = formData.patientId === patient._id;
                                    if (!isSelected) {
                                      e.currentTarget.style.backgroundColor = '';
                                    }
                                  }}
                                >
                                  <div style={{ fontWeight: formData.patientId === patient._id ? '600' : 'normal' }}>
                                    {patient.metadata?.name} (Age: {formatPatientAge(patient)})
                                  </div>
                                  {patient.spec?.phone && (
                                    <div style={{ fontSize: '14px', marginTop: '2px' }} className="dropdown-phone">
                                      {patient.spec.phone}
                                    </div>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                  {fieldErrors.patientId && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {fieldErrors.patientId}
                    </small>
                  )}
                </div>

                {/* New Patient Fields - shown when createNewPatient is true */}
                {formData.createNewPatient && (
                  <>
                    <div className="form-group new-patient-section" style={{ marginTop: '16px', padding: '12px', borderRadius: '6px' }}>
                      <div className="new-patient-header" style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                        New Patient Information
                      </div>

                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label>
                          Name
                        </label>
                        <input
                          type="text"
                          value={formData.patientName}
                          onChange={(e) => setFormData({ ...formData, patientName: e.target.value })}
                          placeholder="Enter patient name"
                          className={`form-input ${fieldErrors.newPatientName ? 'input-error' : ''}`}
                        />
                        {fieldErrors.newPatientName && (
                          <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                            {fieldErrors.newPatientName}
                          </small>
                        )}
                      </div>

                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label>
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={formData.patientPhone}
                          onChange={(e) => {
                            const numeric = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setFormData({ ...formData, patientPhone: numeric });
                            const phoneError = validatePhone(numeric);
                            setPatientPhoneError(phoneError);
                          }}
                          placeholder="Phone number (10 digits)"
                          maxLength={10}
                          className={`form-input ${fieldErrors.newPatientPhone || patientPhoneError ? 'input-error' : ''
                            }`}
                        />
                        {(fieldErrors.newPatientPhone || patientPhoneError) && (
                          <div style={{ color: '#f44336', fontSize: '14px', marginTop: '4px' }}>
                            {fieldErrors.newPatientPhone || patientPhoneError}
                          </div>
                        )}
                      </div>

                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label>
                          Gender
                        </label>
                        <select
                          value={formData.patientGender}
                          onChange={(e) => setFormData({ ...formData, patientGender: e.target.value })}
                          className={`form-input ${fieldErrors.newPatientGender ? 'input-error' : ''}`}
                        >
                          <option value="">Select gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                        {fieldErrors.newPatientGender && (
                          <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                            {fieldErrors.newPatientGender}
                          </small>
                        )}
                      </div>

                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label>
                          Date of Birth
                        </label>
                        <input
                          type="date"
                          value={formData.patientDateOfBirth}
                          onChange={(e) => setFormData({ ...formData, patientDateOfBirth: e.target.value })}
                          max={getMaxDateToday()}
                          className={`form-input ${fieldErrors.newPatientDob ? 'input-error' : ''}`}
                        />
                        {fieldErrors.newPatientDob && (
                          <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                            {fieldErrors.newPatientDob}
                          </small>
                        )}
                      </div>

                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label>ID Number</label>
                        <input
                          type="text"
                          value={formData.patientIdNumber}
                          onChange={(e) => setFormData({ ...formData, patientIdNumber: e.target.value })}
                          placeholder="Aadhar, PAN, Student ID, etc."
                          className="form-input"
                        />
                        <small style={{ color: isDarkTheme ? '#94a3b8' : '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                          Enter ID number (Aadhar, PAN, Student ID, etc.)
                        </small>
                      </div>

                      <div style={{
                        marginTop: '16px',
                        borderTop: isDarkTheme ? '1px solid #334155' : '1px solid #eee',
                        paddingTop: '12px',
                        marginBottom: '12px'
                      }}>
                        {/* <h4 style={{ 
                          marginBottom: '12px', 
                          fontSize: '16px', 
                          fontWeight: '600',
                          color: isDarkTheme ? '#f1f5f9' : '#1e293b'
                        }}>Contact Person</h4>
                        {fieldErrors.newPatientContactPersonPhone && (
                          <div style={{ 
                            marginBottom: '12px', 
                            padding: '8px 12px', 
                            backgroundColor: isDarkTheme ? '#7f1d1d' : '#fee', 
                            border: isDarkTheme ? '1px solid #991b1b' : '1px solid #fcc', 
                            borderRadius: '4px' 
                          }}>
                            <small style={{ color: isDarkTheme ? '#fca5a5' : '#e74c3c', fontSize: '14px' }}>
                              {fieldErrors.newPatientContactPersonPhone}
                            </small>
                          </div>
                        )} */}
                      </div>

                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label>
                          Contact Person Name
                        </label>
                        <input
                          type="text"
                          value={formData.patientContactPersonName}
                          onChange={(e) => setFormData({ ...formData, patientContactPersonName: e.target.value })}
                          placeholder="Contact person name"
                          className="form-input"
                        />
                      </div>

                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label>
                          Contact Person Number <span className="required-star">*</span>
                        </label>
                        <input
                          type="tel"
                          value={formData.patientContactPersonPhone}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setFormData({ ...formData, patientContactPersonPhone: value });
                            const phoneError = validatePhone(value);
                            if (phoneError) {
                              setFieldErrors((prev) => ({
                                ...prev,
                                newPatientContactPersonPhone: phoneError,
                              }));
                            } else {
                              setFieldErrors((prev) => {
                                const newErrors = { ...prev };
                                delete newErrors.newPatientContactPersonPhone;
                                return newErrors;
                              });
                            }
                          }}
                          placeholder="Contact person number (10 digits)"
                          maxLength={10}
                          className={`form-input ${fieldErrors.newPatientContactPersonPhone ? 'input-error' : ''}`}
                        />
                        {fieldErrors.newPatientContactPersonPhone && (
                          <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                            {fieldErrors.newPatientContactPersonPhone}
                          </small>
                        )}
                      </div>

                      {/* Vital Information Section */}
                      {vitalFields && vitalFields.spec.fields.length > 0 && (
                        <div style={{
                          marginTop: '16px',
                          borderTop: isDarkTheme ? '1px solid #334155' : '1px solid #eee',
                          paddingTop: '12px'
                        }}>
                          <button
                            type="button"
                            onClick={() => setShowVitalInfo(!showVitalInfo)}
                            style={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '10px 12px',
                              backgroundColor: isDarkTheme ? '#1e293b' : '#f8f9fa',
                              border: isDarkTheme ? '1px solid #334155' : '1px solid #ddd',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '16px',
                              fontWeight: '600',
                              color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#e9ecef';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = isDarkTheme ? '#1e293b' : '#f8f9fa';
                            }}
                          >
                            <span>Vital Information</span>
                            <span style={{ fontSize: '20px', transition: 'transform 0.2s', transform: showVitalInfo ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                              ▼
                            </span>
                          </button>

                          {showVitalInfo && (
                            <div style={{ marginTop: '12px' }}>
                              {vitalFields.spec.fields.map((field, index) => (
                                <div key={index} className="form-group" style={{ marginBottom: '12px' }}>
                                  <label style={{ fontSize: '15px', fontWeight: '500' }}>
                                    {field.fieldLabel}
                                  </label>
                                  {field.fieldType === 'NUMBER' ? (
                                    <input
                                      type="number"
                                      value={formData.vitalInfo[field.fieldKey] || ''}
                                      onChange={(e) => {
                                        const value = e.target.value === '' ? '' : Number(e.target.value);
                                        setFormData({
                                          ...formData,
                                          vitalInfo: {
                                            ...formData.vitalInfo,
                                            [field.fieldKey]: value,
                                          },
                                        });
                                      }}
                                      placeholder={`Enter ${field.fieldLabel.toLowerCase()}`}
                                      className="form-input"
                                    />
                                  ) : (
                                    <input
                                      type="text"
                                      value={formData.vitalInfo[field.fieldKey] || ''}
                                      onChange={(e) => {
                                        setFormData({
                                          ...formData,
                                          vitalInfo: {
                                            ...formData.vitalInfo,
                                            [field.fieldKey]: e.target.value,
                                          },
                                        });
                                      }}
                                      placeholder={`Enter ${field.fieldLabel.toLowerCase()}`}
                                      className="form-input"
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {formData.pickupLocation && (
                        <div className="new-patient-info" style={{
                          marginTop: '8px',
                          padding: '8px',
                          borderRadius: '4px',
                          fontSize: '14px',
                          color: isDarkTheme ? '#86efac' : '#2e7d32',
                          backgroundColor: isDarkTheme ? '#1e3a1e' : '#e8f5e9'
                        }}>
                          ℹ️ Patient location will be set from pickup location
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, createNewPatient: false, patientName: '', patientPhone: '', patientGender: '', patientDateOfBirth: '', patientContactPersonName: '', patientContactPersonPhone: '', vitalInfo: {} });
                          setPatientPhoneError('');
                        }}
                        className="btn-cancel"
                        style={{
                          marginTop: '8px',
                          padding: '6px 12px',
                          fontSize: '14px',
                        }}
                      >
                        Cancel - Select Existing Patient
                      </button>
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label>
                    Priority
                    <span className="required-star">*</span>
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: isDarkTheme ? '1px solid #475569' : '1px solid #ddd',
                      borderRadius: '6px',
                      backgroundColor: isDarkTheme ? '#1e293b' : 'white',
                      color: isDarkTheme ? '#f1f5f9' : '#000'
                    }}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    Booking Type
                    <span className="required-star">*</span>
                  </label>
                  <select
                    value={formData.bookingType}
                    onChange={(e) => handleBookingTypeChange(e.target.value as 'to_hospital' | 'from_hospital')}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: isDarkTheme ? '1px solid #475569' : '1px solid #ddd',
                      borderRadius: '6px',
                      backgroundColor: isDarkTheme ? '#1e293b' : 'white',
                      color: isDarkTheme ? '#f1f5f9' : '#000'
                    }}
                  >
                    <option value="to_hospital">To Hospital (Pick from location → Drop at hospital)</option>
                    <option value="from_hospital">From Hospital (Pick from hospital → Drop at location)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Medical Condition</label>
                  <input
                    type="text"
                    value={formData.medicalCondition}
                    onChange={(e) => setFormData({ ...formData, medicalCondition: e.target.value })}
                    placeholder="Brief description of patient condition"
                  />
                </div>

                <div className="form-group">
                  <label>
                    Scheduled Pickup Time
                    <span className="required-star">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.scheduledPickupTime}
                    onChange={(e) => setFormData({ ...formData, scheduledPickupTime: e.target.value })}
                    className={fieldErrors.scheduledPickupTime ? 'input-error' : ''}
                  />
                  {fieldErrors.scheduledPickupTime && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {fieldErrors.scheduledPickupTime}
                    </small>
                  )}
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>
                    Pickup Location
                    <span className="required-star">*</span>
                  </label>
                  <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Search for a place or click on the map to set pickup location</p>
                  <div className={`map-input-wrapper ${fieldErrors.pickupLocation ? 'input-error' : ''}`}>
                    <PlaceSearch
                      onPlaceSelect={(lat, lng, address) => {
                        setFormData((prev) => ({
                          ...prev,
                          pickupLocation: { latitude: lat, longitude: lng, address },
                        }));
                      }}
                      placeholder="Search for pickup location..."
                      currentLocation={formData.pickupLocation}
                    />
                    <GoogleMap
                      center={
                        formData.pickupLocation
                          ? { lat: formData.pickupLocation.latitude, lng: formData.pickupLocation.longitude }
                          : { lat: 28.6139, lng: 77.209 }
                      }
                      zoom={formData.pickupLocation ? 13 : 10}
                      location={formData.pickupLocation}
                      onLocationChange={(lat, lng) => {
                        setFormData((prev) => ({
                          ...prev,
                          pickupLocation: { latitude: lat, longitude: lng },
                        }));
                      }}
                      height="250px"
                    />
                  </div>
                  {formData.pickupLocation && (
                    <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                      <strong>Selected:</strong> {formData.pickupLocation.latitude.toFixed(6)}, {formData.pickupLocation.longitude.toFixed(6)}
                      {formData.pickupLocation.address && ` - ${formData.pickupLocation.address}`}
                    </div>
                  )}
                  {formData.pickupLocation && (
                    <div className="form-group" style={{ marginTop: '12px' }}>
                      <label>Pickup Address Details (Flat/Area/Landmark)</label>
                      <input
                        type="text"
                        value={formData.pickupAddressDetails}
                        onChange={(e) => setFormData({ ...formData, pickupAddressDetails: e.target.value })}
                        placeholder="e.g., Flat 201, Building A, Near Metro Station"
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: isDarkTheme ? '1px solid #475569' : '1px solid #ddd',
                          borderRadius: '6px',
                          backgroundColor: isDarkTheme ? '#1e293b' : 'white',
                          color: isDarkTheme ? '#f1f5f9' : '#000'
                        }}
                      />
                      <p style={{ fontSize: '13px', color: '#999', marginTop: '4px' }}>
                        Add specific details like flat number, building name, or nearby landmarks
                      </p>
                    </div>
                  )}
                  {fieldErrors.pickupLocation && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {fieldErrors.pickupLocation}
                    </small>
                  )}
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>
                    Dropoff Location
                    <span className="required-star">*</span>
                  </label>
                  <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Search for a place or click on the map to set dropoff location</p>
                  <div className={`map-input-wrapper ${fieldErrors.dropoffLocation ? 'input-error' : ''}`}>
                    <PlaceSearch
                      onPlaceSelect={(lat, lng, address) => {
                        setFormData((prev) => ({
                          ...prev,
                          dropoffLocation: { latitude: lat, longitude: lng, address },
                        }));
                      }}
                      placeholder="Search for dropoff location..."
                      currentLocation={formData.dropoffLocation}
                    />
                    <GoogleMap
                      center={
                        formData.dropoffLocation
                          ? { lat: formData.dropoffLocation.latitude, lng: formData.dropoffLocation.longitude }
                          : { lat: 28.6139, lng: 77.209 }
                      }
                      zoom={formData.dropoffLocation ? 13 : 10}
                      location={formData.dropoffLocation}
                      onLocationChange={(lat, lng) => {
                        setFormData((prev) => ({
                          ...prev,
                          dropoffLocation: { latitude: lat, longitude: lng },
                        }));
                      }}
                      height="250px"
                    />
                  </div>
                  {formData.dropoffLocation && (
                    <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                      <strong>Selected:</strong> {formData.dropoffLocation.latitude.toFixed(6)}, {formData.dropoffLocation.longitude.toFixed(6)}
                      {formData.dropoffLocation.address && ` - ${formData.dropoffLocation.address}`}
                    </div>
                  )}
                  {fieldErrors.dropoffLocation && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {fieldErrors.dropoffLocation}
                    </small>
                  )}
                  {formData.dropoffLocation && (
                    <div className="form-group" style={{ marginTop: '12px' }}>
                      <label>Dropoff Address Details (Flat/Area/Landmark)</label>
                      <input
                        type="text"
                        value={formData.dropoffAddressDetails}
                        onChange={(e) => setFormData({ ...formData, dropoffAddressDetails: e.target.value })}
                        placeholder="e.g., Flat 201, Building A, Near Metro Station"
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: isDarkTheme ? '1px solid #475569' : '1px solid #ddd',
                          borderRadius: '6px',
                          backgroundColor: isDarkTheme ? '#1e293b' : 'white',
                          color: isDarkTheme ? '#f1f5f9' : '#000'
                        }}
                      />
                      <p style={{ fontSize: '13px', color: '#999', marginTop: '4px' }}>
                        Add specific details like flat number, building name, or nearby landmarks
                      </p>
                    </div>
                  )}
                </div>

                {/* Ambulance (for tariff calculation) */}
                <div className="form-group">
                  <label>Ambulance</label>
                  <div ref={createAmbulanceDropdownRef} style={{ position: 'relative', width: '100%', zIndex: showCreateAmbulanceDropdown ? 10001 : 'auto' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateAmbulanceDropdown(!showCreateAmbulanceDropdown);
                        if (!showCreateAmbulanceDropdown) {
                          setCreateAmbulanceSearchTerm('');
                        }
                      }}
                      className="custom-dropdown-trigger"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: isDarkTheme ? '1px solid #475569' : '1px solid #ddd',
                        borderRadius: '6px',
                        backgroundColor: isDarkTheme ? '#1e293b' : 'white',
                        color: isDarkTheme ? '#f1f5f9' : '#000',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '16px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <span>
                        {(() => {
                          const selectedAmbulance = ambulances.find((a) => a._id === selectedCreateAmbulanceId);
                          return selectedAmbulance
                            ? `${selectedAmbulance.metadata.name} (${selectedAmbulance.spec.licensePlate})`
                            : 'Select an ambulance';
                        })()}
                      </span>
                      <span style={{ fontSize: '14px' }}>{showCreateAmbulanceDropdown ? '▲' : '▼'}</span>
                    </button>
                    {showCreateAmbulanceDropdown && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          border: isDarkTheme ? '1px solid #475569' : '1px solid #ddd',
                          borderRadius: '0 0 6px 6px',
                          backgroundColor: isDarkTheme ? '#1e293b' : 'white',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          zIndex: 10002,
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Search ambulance by name, license plate, or type..."
                          value={createAmbulanceSearchTerm}
                          onChange={(e) => setCreateAmbulanceSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="search-input"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: 'none',
                            borderBottom: '1px solid',
                            borderRadius: '6px 6px 0 0',
                            fontSize: '16px',
                            outline: 'none',
                          }}
                          autoFocus
                        />
                        <div
                          style={{
                            maxHeight: '250px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                          }}
                        >
                          {(() => {
                            const searchLower = createAmbulanceSearchTerm.toLowerCase().trim();

                            const filteredAmbulances = ambulances.filter((a) => {
                              if (a.spec.status === 'on_duty') return false;

                              if (searchLower !== '') {
                                const name = (a.metadata.name || '').toLowerCase();
                                const licensePlate = (a.spec.licensePlate || '').toLowerCase();
                                const type = (a.spec.type || '').toLowerCase();
                                if (!name.includes(searchLower) && !licensePlate.includes(searchLower) && !type.includes(searchLower)) {
                                  return false;
                                }
                              }

                              return true;
                            });

                            if (filteredAmbulances.length === 0) {
                              return (
                                <div
                                  style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    color: '#666',
                                    fontSize: '16px',
                                  }}
                                >
                                  No ambulances found
                                </div>
                              );
                            }

                            // Pre-compute distance and sort by nearest first
                            const sortedCreateAmbulances = filteredAmbulances
                              .map((amb) => {
                                let dist: number | null = null;
                                const loc = getAmbulanceLiveLocation_(amb);
                                if (loc && formData.pickupLocation) {
                                  dist = calculateDistanceKm(loc.latitude, loc.longitude, formData.pickupLocation.latitude, formData.pickupLocation.longitude);
                                }
                                return { ambulance: amb, distance: dist };
                              })
                              .sort((a, b) => {
                                if (a.distance === null && b.distance === null) return 0;
                                if (a.distance === null) return 1;
                                if (b.distance === null) return -1;
                                return a.distance - b.distance;
                              });

                            return sortedCreateAmbulances.map(({ ambulance, distance: distFromPickup }) => {
                              return (
                                <div
                                  key={ambulance._id}
                                  onClick={() => {
                                    setSelectedCreateAmbulanceId(ambulance._id);
                                    setBookingAmountManuallyEdited(false);
                                    setShowCreateAmbulanceDropdown(false);
                                    setCreateAmbulanceSearchTerm('');
                                  }}
                                  className={`custom-dropdown-option ${selectedCreateAmbulanceId === ambulance._id ? 'selected' : ''}`}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0',
                                    fontSize: '16px',
                                    transition: 'background-color 0.2s',
                                  }}
                                >
                                  <div style={{ fontWeight: selectedCreateAmbulanceId === ambulance._id ? '600' : 'normal' }}>
                                    {ambulance.metadata.name} ({ambulance.spec.licensePlate})
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#666', marginTop: '2px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                    {ambulance.spec.type && (
                                      <span>Type: {getAmbulanceTypeDisplayName(ambulance.spec.type)}</span>
                                    )}
                                    {distFromPickup !== null && (
                                      <span style={{ color: '#2563eb', fontWeight: 500 }}>
                                        Distance from pickup: ~{distFromPickup.toFixed(1)} km
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Booking Amount */}
                <div className="form-group">
                  <label>Booking Amount (&#8377;)</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={formData.bookingAmount}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setFormData({ ...formData, bookingAmount: value });
                        setBookingAmountManuallyEdited(true);
                      }}
                      placeholder={isCalculatingAmount ? 'Calculating...' : 'Auto or enter manually'}
                      style={{
                        width: '100%',
                        padding: '10px',
                        paddingRight: isCalculatingAmount ? '40px' : '10px',
                        border: isDarkTheme ? '1px solid #475569' : '1px solid #ddd',
                        borderRadius: '6px',
                        backgroundColor: isDarkTheme ? '#1e293b' : 'white',
                        color: isDarkTheme ? '#f1f5f9' : '#000',
                        boxSizing: 'border-box',
                      }}
                    />
                    {isCalculatingAmount && (
                      <span style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '12px',
                        color: isDarkTheme ? '#94a3b8' : '#6b7280',
                      }}>
                        <span className="loading-spinner btn-spinner" aria-hidden />
                      </span>
                    )}
                  </div>
                  {calculatedDistance !== null && selectedCreateAmbulanceId && (
                    <p style={{
                      margin: '4px 0 0',
                      fontSize: '12px',
                      color: isDarkTheme ? '#94a3b8' : '#6b7280',
                    }}>
                      Distance: ~{calculatedDistance.toFixed(1)} km
                      {(() => {
                        const selAmb = ambulances.find((a) => a._id === selectedCreateAmbulanceId);
                        return selAmb?.spec?.type ? ` | Type: ${getAmbulanceTypeDisplayName(selAmb.spec.type)}` : '';
                      })()}
                      {bookingAmountManuallyEdited && (
                        <> &middot; <span
                          style={{ color: isDarkTheme ? '#60a5fa' : '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => {
                            setBookingAmountManuallyEdited(false);
                          }}
                        >recalculate</span></>
                      )}
                    </p>
                  )}
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: isDarkTheme ? '1px solid #475569' : '1px solid #ddd',
                      borderRadius: '6px',
                      backgroundColor: isDarkTheme ? '#1e293b' : 'white',
                      color: isDarkTheme ? '#f1f5f9' : '#000'
                    }}
                  />
                </div>
              </div>

              <div className="drawer-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)} disabled={createBookingSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={createBookingSubmitting}>
                  {createBookingSubmitting ? (
                    <>
                      <span className="loading-spinner btn-spinner" aria-hidden />
                      {editingBooking ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingBooking ? 'Update Booking' : 'Create Booking'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Ambulance/Staff Drawer */}
      {showAssignModal && assigningBooking && (
        <div className="drawer-overlay" onClick={handleCloseAssignModal}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">
                {assignMode === 'reassign' ? 'Reassign Ambulance & Staff' : 'Assign Ambulance & Staff'}
              </h3>
              <button className="drawer-close" onClick={handleCloseAssignModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleAssignSubmit} className="drawer-form">
              <div className="drawer-body" style={{ position: 'relative' }}>
                {assignError && <div className="error-message">{assignError}</div>}

                <div className="form-group">
                  <label>
                    Booking Type
                    <span className="required-star">*</span>
                  </label>
                  <select
                    value={assignFormData.bookingType}
                    onChange={(e) => setAssignFormData({ ...assignFormData, bookingType: e.target.value as any })}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                  >
                    <option value="to_hospital">To Hospital (Pick from location → Drop at hospital)</option>
                    <option value="from_hospital">From Hospital (Pick from hospital → Drop at location)</option>
                  </select>
                </div>

                {(showAmbulanceDropdown || showPilotDropdown) && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 9999,
                      cursor: 'not-allowed',
                      backgroundColor: 'rgba(0, 0, 0, 0.01)',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (showAmbulanceDropdown) setShowAmbulanceDropdown(false);
                      if (showPilotDropdown) setShowPilotDropdown(false);
                    }}
                  />
                )}

                <div className="form-group">
                  <label>
                    Ambulance
                    <span className="required-star">*</span>
                  </label>
                  <div ref={ambulanceDropdownRef} style={{ position: 'relative', width: '100%', zIndex: showAmbulanceDropdown ? 10001 : 'auto' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAmbulanceDropdown(!showAmbulanceDropdown);
                        if (!showAmbulanceDropdown) {
                          setAmbulanceSearchTerm('');
                        }
                      }}
                      className="custom-select-button"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid',
                        borderRadius: '6px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '16px',
                      }}
                    >
                      <span>
                        {(() => {
                          const selectedAmbulance = ambulances.find((a) => a._id === assignFormData.ambulanceId);
                          return selectedAmbulance
                            ? `${selectedAmbulance.metadata.name} (${selectedAmbulance.spec.licensePlate})`
                            : 'Select an ambulance';
                        })()}
                      </span>
                      <span style={{ fontSize: '14px' }}>{showAmbulanceDropdown ? '▲' : '▼'}</span>
                    </button>
                    {showAmbulanceDropdown && (
                      <div
                        className="custom-dropdown-menu"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          marginTop: '4px',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          zIndex: 10000,
                          maxHeight: '300px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          isolation: 'isolate',
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Search ambulance by name, license plate, or type..."
                          value={ambulanceSearchTerm}
                          onChange={(e) => setAmbulanceSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="search-input"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: 'none',
                            borderBottom: '1px solid',
                            borderRadius: '6px 6px 0 0',
                            fontSize: '16px',
                            outline: 'none',
                          }}
                          autoFocus
                        />
                        <div
                          style={{
                            maxHeight: '250px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                          }}
                        >
                          {(() => {
                            const searchLower = ambulanceSearchTerm.toLowerCase().trim();

                            const filteredAmbulances = ambulances.filter((a) => {
                              // Exclude ambulances that are on duty
                              if (a.spec.status === 'on_duty') return false;

                              // Filter by search term
                              if (searchLower !== '') {
                                const name = (a.metadata.name || '').toLowerCase();
                                const licensePlate = (a.spec.licensePlate || '').toLowerCase();
                                const type = (a.spec.type || '').toLowerCase();
                                if (!name.includes(searchLower) && !licensePlate.includes(searchLower) && !type.includes(searchLower)) {
                                  return false;
                                }
                              }

                              return true;
                            });

                            if (filteredAmbulances.length === 0) {
                              return (
                                <div
                                  style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    color: '#666',
                                    fontSize: '16px',
                                  }}
                                >
                                  No ambulances found
                                </div>
                              );
                            }

                            // Pre-compute distance and sort by nearest first
                            const assignPickup = assigningBooking?.spec?.pickupLocation;
                            const sortedAmbulances = filteredAmbulances
                              .map((amb) => {
                                let dist: number | null = null;
                                const loc = getAmbulanceLiveLocation_(amb);
                                if (loc && assignPickup) {
                                  dist = calculateDistanceKm(loc.latitude, loc.longitude, assignPickup.latitude, assignPickup.longitude);
                                }
                                return { ambulance: amb, distance: dist };
                              })
                              .sort((a, b) => {
                                if (a.distance === null && b.distance === null) return 0;
                                if (a.distance === null) return 1;
                                if (b.distance === null) return -1;
                                return a.distance - b.distance;
                              });

                            return sortedAmbulances.map(({ ambulance, distance: assignDistFromPickup }) => {
                              const isAvailable = ambulance.spec.status === 'available';
                              const statusClass = isAvailable ? 'ambulance-status-available' : 'ambulance-status-other';

                              const handleSelect = () => {
                                const val = ambulance._id;
                                const selectedAmbulance = ambulances.find((a) => a._id === val);

                                // Get filtering data for validation
                                const activeStatuses = ['pending', 'assigned', 'in_transit', 'patient_picked', 'patient_dropped'];
                                const lockedStatuses = ['in_transit', 'patient_picked', 'patient_dropped'];

                                // Get ambulances that are on duty
                                const ambulancesOnDuty = new Set(
                                  bookings
                                    .filter((b) =>
                                      b._id !== assigningBooking?._id &&
                                      b.spec.ambulanceId &&
                                      activeStatuses.includes(b.spec.status)
                                    )
                                    .map((b) => b.spec.ambulanceId)
                                );

                                // Get pilots assigned to ambulances that are on duty
                                const pilotsOnDutyAmbulances = new Set(
                                  bookings
                                    .filter((b) =>
                                      b._id !== assigningBooking?._id &&
                                      b.spec.ambulanceId &&
                                      ambulancesOnDuty.has(b.spec.ambulanceId) &&
                                      b.spec.pilotId &&
                                      activeStatuses.includes(b.spec.status)
                                    )
                                    .map((b) => b.spec.pilotId)
                                );

                                // Get staff assigned to ambulances that are on duty
                                const staffOnDutyAmbulances = new Set(
                                  bookings
                                    .filter((b) =>
                                      b._id !== assigningBooking?._id &&
                                      b.spec.ambulanceId &&
                                      ambulancesOnDuty.has(b.spec.ambulanceId) &&
                                      b.spec.assignedStaffIds &&
                                      b.spec.assignedStaffIds.length > 0 &&
                                      activeStatuses.includes(b.spec.status)
                                    )
                                    .flatMap((b) => b.spec.assignedStaffIds || [])
                                );

                                const assignedPilotIds = new Set(
                                  bookings
                                    .filter((b) =>
                                      b._id !== assigningBooking?._id &&
                                      b.spec.pilotId &&
                                      activeStatuses.includes(b.spec.status)
                                    )
                                    .map((b) => b.spec.pilotId)
                                );

                                const lockedPilotIds = new Set(
                                  bookings
                                    .filter((b) =>
                                      b._id !== assigningBooking?._id &&
                                      b.spec.pilotId &&
                                      lockedStatuses.includes(b.spec.status)
                                    )
                                    .map((b) => b.spec.pilotId)
                                );

                                const assignedStaffIds = new Set(
                                  bookings
                                    .filter((b) =>
                                      b._id !== assigningBooking?._id &&
                                      b.spec.assignedStaffIds &&
                                      b.spec.assignedStaffIds.length > 0 &&
                                      activeStatuses.includes(b.spec.status)
                                    )
                                    .flatMap((b) => b.spec.assignedStaffIds || [])
                                );

                                const lockedStaffIds = new Set(
                                  bookings
                                    .filter((b) =>
                                      b._id !== assigningBooking?._id &&
                                      b.spec.assignedStaffIds &&
                                      b.spec.assignedStaffIds.length > 0 &&
                                      lockedStatuses.includes(b.spec.status)
                                    )
                                    .flatMap((b) => b.spec.assignedStaffIds || [])
                                );

                                // Get set of user IDs who are checked in today
                                const presentTodayUserIds = new Set(
                                  todayAttendance
                                    .filter(a => a.spec.status === 'checkedIn')
                                    .map(a => a.spec.userId)
                                );

                                setAssignFormData((prev) => {
                                  const updates: any = { ambulanceId: val };

                                  // Auto-populate pilot if ambulance has one assigned AND pilot meets criteria
                                  if (selectedAmbulance?.spec?.assignedPilot?.userId) {
                                    const pilotId = selectedAmbulance.spec.assignedPilot.userId;

                                    // Check if pilot meets all criteria
                                    const isPresentToday = presentTodayUserIds.has(pilotId);
                                    const isNotAssigned = !assignedPilotIds.has(pilotId);
                                    const isNotOnDutyAmbulance = !pilotsOnDutyAmbulances.has(pilotId);
                                    const isNotLocked = !lockedPilotIds.has(pilotId);
                                    const isCurrentBooking = pilotId === assigningBooking?.spec.pilotId;

                                    if (isPresentToday && (isNotAssigned || isCurrentBooking) && isNotOnDutyAmbulance && isNotLocked) {
                                      updates.pilotId = pilotId;
                                    }
                                  }

                                  // Auto-populate staff if ambulance has assigned staff AND they meet criteria
                                  if (selectedAmbulance?.spec?.assignedStaffs && selectedAmbulance.spec.assignedStaffs.length > 0) {
                                    const staffToAdd: string[] = [];

                                    selectedAmbulance.spec.assignedStaffs.forEach((assignedStaff) => {
                                      const staffId = assignedStaff.userId;

                                      // Find the staff member to check their details
                                      const staffMember = staff.find((s) => (s._id || s.id) === staffId);
                                      if (staffMember) {
                                        // Check if staff is not a pilot/driver
                                        const staffCategory = (staffMember.spec?.staffCategory || '').toLowerCase();
                                        const roles = (staffMember.spec?.roles || []).map((r: string) => r.toLowerCase());
                                        const isPilot = staffCategory === 'pilot' || staffCategory === 'driver' || roles.some((r) => r.includes('pilot') || r.includes('driver'));

                                        if (!isPilot) {
                                          // Check if staff meets all criteria
                                          const isPresentToday = presentTodayUserIds.has(staffId);
                                          const isNotAssigned = !assignedStaffIds.has(staffId);
                                          const isNotOnDutyAmbulance = !staffOnDutyAmbulances.has(staffId);
                                          const isNotLocked = !lockedStaffIds.has(staffId);
                                          const isCurrentBooking = assigningBooking?.spec.assignedStaffIds?.includes(staffId);

                                          if (isPresentToday && (isNotAssigned || isCurrentBooking) && isNotOnDutyAmbulance && isNotLocked) {
                                            // Add staff to list if not already present
                                            if (!prev.assignedStaffIds.includes(staffId) && !staffToAdd.includes(staffId)) {
                                              staffToAdd.push(staffId);
                                            }
                                          }
                                        }
                                      }
                                    });

                                    if (staffToAdd.length > 0) {
                                      updates.assignedStaffIds = [...prev.assignedStaffIds, ...staffToAdd];
                                    }
                                  }

                                  return { ...prev, ...updates };
                                });
                                setShowAmbulanceDropdown(false);
                                setAmbulanceSearchTerm('');
                              };

                              return (
                                <div
                                  key={ambulance._id}
                                  onClick={handleSelect}
                                  className={`custom-dropdown-option ${statusClass} ${assignFormData.ambulanceId === ambulance._id ? 'selected' : ''}`}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0',
                                    fontSize: '16px',
                                    transition: 'background-color 0.2s',
                                  }}
                                >
                                  <div style={{ fontWeight: assignFormData.ambulanceId === ambulance._id ? '600' : 'normal' }}>
                                    {ambulance.metadata.name} ({ambulance.spec.licensePlate})
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#666', marginTop: '2px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                    {ambulance.spec.type && (
                                      <span>Type: {getAmbulanceTypeDisplayName(ambulance.spec.type)}</span>
                                    )}
                                    {assignDistFromPickup !== null && (
                                      <span style={{ color: '#2563eb', fontWeight: 500 }}>
                                        Distance from pickup: ~{assignDistFromPickup.toFixed(1)} km
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    Pilot
                    <span className="required-star">*</span>
                  </label>
                  <div ref={pilotDropdownRef} style={{ position: 'relative', width: '100%', zIndex: showPilotDropdown ? 10001 : 'auto' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPilotDropdown(!showPilotDropdown);
                        if (!showPilotDropdown) {
                          setPilotSearchTerm('');
                        }
                      }}
                      className="custom-select-button"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid',
                        borderRadius: '6px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '16px',
                      }}
                    >
                      <span>
                        {(() => {
                          const selectedPilot = staff.find((s) => (s._id || s.id) === assignFormData.pilotId);
                          return selectedPilot
                            ? selectedPilot.metadata?.name || selectedPilot.name
                            : 'Select a pilot';
                        })()}
                      </span>
                      <span style={{ fontSize: '14px' }}>{showPilotDropdown ? '▲' : '▼'}</span>
                    </button>
                    {showPilotDropdown && (
                      <div
                        className="custom-dropdown-menu"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          marginTop: '4px',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          zIndex: 10000,
                          maxHeight: '300px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          isolation: 'isolate',
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Search pilot by name, phone, or role..."
                          value={pilotSearchTerm}
                          onChange={(e) => setPilotSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="search-input"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: 'none',
                            borderBottom: '1px solid',
                            borderRadius: '6px 6px 0 0',
                            fontSize: '16px',
                            outline: 'none',
                          }}
                          autoFocus
                        />
                        <div
                          style={{
                            maxHeight: '250px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                          }}
                        >
                          {(() => {
                            // Get list of pilots assigned to active bookings (excluding current booking)
                            // Lock pilots assigned to in_transit or arrived bookings
                            const activeStatuses = ['pending', 'assigned', 'in_transit', 'patient_picked', 'patient_dropped'];
                            const lockedStatuses = ['in_transit', 'patient_picked', 'patient_dropped'];

                            // Get ambulances that are on duty (have active bookings)
                            const ambulancesOnDuty = new Set(
                              bookings
                                .filter((b) =>
                                  b._id !== assigningBooking?._id &&
                                  b.spec.ambulanceId &&
                                  activeStatuses.includes(b.spec.status)
                                )
                                .map((b) => b.spec.ambulanceId)
                            );

                            // Get pilots assigned to ambulances that are on duty
                            const pilotsOnDutyAmbulances = new Set(
                              bookings
                                .filter((b) =>
                                  b._id !== assigningBooking?._id &&
                                  b.spec.ambulanceId &&
                                  ambulancesOnDuty.has(b.spec.ambulanceId) &&
                                  b.spec.pilotId &&
                                  activeStatuses.includes(b.spec.status)
                                )
                                .map((b) => b.spec.pilotId)
                            );

                            const assignedPilotIds = new Set(
                              bookings
                                .filter((b) =>
                                  b._id !== assigningBooking?._id &&
                                  b.spec.pilotId &&
                                  activeStatuses.includes(b.spec.status)
                                )
                                .map((b) => b.spec.pilotId)
                            );
                            // Lock pilots from in_transit/arrived bookings
                            const lockedPilotIds = new Set(
                              bookings
                                .filter((b) =>
                                  b._id !== assigningBooking?._id &&
                                  b.spec.pilotId &&
                                  lockedStatuses.includes(b.spec.status)
                                )
                                .map((b) => b.spec.pilotId)
                            );

                            const searchLower = pilotSearchTerm.toLowerCase().trim();

                            // Get set of user IDs who are checked in today
                            const presentTodayUserIds = new Set(
                              todayAttendance
                                .filter(a => a.spec.status === 'checkedIn')
                                .map(a => a.spec.userId)
                            );

                            const filteredPilots = staff.filter((s) => {
                              const pilotId = s._id || s.id || '';

                              // Only show pilots who are present today
                              if (!presentTodayUserIds.has(pilotId)) {
                                return false;
                              }

                              // Only show pilots/drivers in pilot dropdown (case-insensitive)
                              // Exclude paramedics explicitly
                              const staffCategory = (s.spec?.staffCategory || '').toLowerCase();
                              const roles = (s.spec?.roles || []).map((r: string) => r.toLowerCase());

                              // Exclude if it's a paramedic
                              if (staffCategory === 'paramedic' || roles.some((r) => r.includes('paramedic'))) {
                                return false;
                              }

                              // Only include if category is pilot or has pilot role
                              const isPilot =
                                staffCategory === 'pilot' || roles.some((r) => r === 'pilot' || r.includes('ambulance pilot') || r.includes('driver'));
                              if (!isPilot) return false;

                              // Filter by search term (name, phone, role)
                              if (searchLower !== '') {
                                const name = (s.metadata?.name || s.name || '').toLowerCase();
                                const phone = (s.spec?.phone || s.phone || '').toLowerCase();
                                const roleStr = (s.spec?.roles || s.roles || []).join(' ').toLowerCase();

                                if (!name.includes(searchLower) &&
                                  !phone.includes(searchLower) &&
                                  !roleStr.includes(searchLower)) {
                                  return false;
                                }
                              }

                              // Show if not assigned to another active booking OR if already assigned to this booking
                              // But exclude if locked (in_transit or arrived) or assigned to an ambulance on duty
                              const pilotStaffId = s._id || s.id || '';
                              const isLocked = lockedPilotIds.has(pilotStaffId);
                              if (isLocked) return false; // Don't show locked pilots
                              const isOnDutyAmbulance = pilotsOnDutyAmbulances.has(pilotStaffId);
                              if (isOnDutyAmbulance) return false; // Don't show pilots assigned to ambulances on duty
                              const isNotAssigned = !assignedPilotIds.has(pilotStaffId);
                              const isCurrentBooking = pilotStaffId === assigningBooking?.spec.pilotId;
                              return isNotAssigned || isCurrentBooking;
                            });

                            if (filteredPilots.length === 0) {
                              return (
                                <div
                                  style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    color: '#666',
                                    fontSize: '16px',
                                  }}
                                >
                                  No pilots found
                                </div>
                              );
                            }

                            return filteredPilots.map((pilot) => {
                              const handleSelect = () => {
                                const val = pilot._id || pilot.id || '';
                                setAssignFormData({ ...assignFormData, pilotId: val });
                                setShowPilotDropdown(false);
                                setPilotSearchTerm('');
                              };

                              return (
                                <div
                                  key={pilot._id || pilot.id}
                                  onClick={handleSelect}
                                  className={`custom-dropdown-option ${assignFormData.pilotId === (pilot._id || pilot.id) ? 'selected' : ''}`}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0',
                                    fontSize: '16px',
                                    transition: 'background-color 0.2s',
                                  }}
                                  onMouseEnter={(e) => {
                                    const isSelected = assignFormData.pilotId === (pilot._id || pilot.id);
                                    if (!isSelected) {
                                      e.currentTarget.style.backgroundColor = '';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    const isSelected = assignFormData.pilotId === (pilot._id || pilot.id);
                                    if (!isSelected) {
                                      e.currentTarget.style.backgroundColor = '';
                                    }
                                  }}
                                >
                                  <div style={{ fontWeight: assignFormData.pilotId === (pilot._id || pilot.id) ? '600' : 'normal' }}>
                                    {(() => {
                                      const name = pilot.metadata?.name || pilot.name || '';
                                      const roles = (pilot.spec?.roles || pilot.roles || []);
                                      const roleStr = roles.length > 0 ? formatRoles(roles) : '';
                                      return roleStr ? `${name} (${roleStr})` : name;
                                    })()}
                                  </div>
                                  {pilot.spec?.phone && (
                                    <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>
                                      {pilot.spec.phone}
                                    </div>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Assign Staff (optional)</label>
                  <div
                    className="permission-list"
                    style={{
                      maxHeight: 'min(600px, 40vh)',
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {(() => {
                      // Get list of staff assigned to active bookings (excluding current booking)
                      // Lock staff assigned to in_transit or arrived bookings
                      const activeStatuses = ['pending', 'assigned', 'in_transit', 'patient_picked', 'patient_dropped'];
                      const lockedStatuses = ['in_transit', 'patient_picked', 'patient_dropped'];

                      // Get ambulances that are on duty (have active bookings)
                      const ambulancesOnDuty = new Set(
                        bookings
                          .filter((b) =>
                            b._id !== assigningBooking?._id &&
                            b.spec.ambulanceId &&
                            activeStatuses.includes(b.spec.status)
                          )
                          .map((b) => b.spec.ambulanceId)
                      );

                      // Get staff assigned to ambulances that are on duty
                      const staffOnDutyAmbulances = new Set(
                        bookings
                          .filter((b) =>
                            b._id !== assigningBooking?._id &&
                            b.spec.ambulanceId &&
                            ambulancesOnDuty.has(b.spec.ambulanceId) &&
                            b.spec.assignedStaffIds &&
                            b.spec.assignedStaffIds.length > 0 &&
                            activeStatuses.includes(b.spec.status)
                          )
                          .flatMap((b) => b.spec.assignedStaffIds || [])
                      );

                      const assignedStaffIds = new Set(
                        bookings
                          .filter((b) =>
                            b._id !== assigningBooking?._id &&
                            b.spec.assignedStaffIds &&
                            b.spec.assignedStaffIds.length > 0 &&
                            activeStatuses.includes(b.spec.status)
                          )
                          .flatMap((b) => b.spec.assignedStaffIds || [])
                      );
                      // Lock staff from in_transit/arrived bookings
                      const lockedStaffIds = new Set(
                        bookings
                          .filter((b) =>
                            b._id !== assigningBooking?._id &&
                            b.spec.assignedStaffIds &&
                            b.spec.assignedStaffIds.length > 0 &&
                            lockedStatuses.includes(b.spec.status)
                          )
                          .flatMap((b) => b.spec.assignedStaffIds || [])
                      );

                      const searchLower = debouncedStaffSearchTerm.toLowerCase().trim();

                      // Get set of user IDs who are checked in today
                      const presentTodayUserIds = new Set(
                        todayAttendance
                          .filter(a => a.spec.status === 'checkedIn')
                          .map(a => a.spec.userId)
                      );

                      const filteredStaff = staff.filter((s) => {
                        const staffMemberId = s._id || s.id || '';

                        // Only show staff who are present today
                        if (!presentTodayUserIds.has(staffMemberId)) {
                          return false;
                        }

                        // Exclude only the pilot selected above (so they don't appear in both Pilot and Assign Staff)
                        const selectedPilotId = assignFormData.pilotId?.trim() || '';
                        if (selectedPilotId && staffMemberId === selectedPilotId) {
                          return false;
                        }

                        // Filter by search term (name, phone, role, category)
                        if (searchLower !== '') {
                          const name = (s.metadata?.name || s.name || '').toLowerCase();
                          const phone = (s.spec?.phone || s.phone || '').toLowerCase();
                          const roles = (s.spec?.roles || s.roles || []).map((r: string) => r.toLowerCase());
                          const roleStr = roles.join(' ').toLowerCase();
                          const category = (s.spec?.staffCategory || '').toLowerCase();

                          if (!name.includes(searchLower) &&
                            !phone.includes(searchLower) &&
                            !roleStr.includes(searchLower) &&
                            !category.includes(searchLower)) {
                            return false;
                          }
                        }

                        // Show if not assigned to another active booking OR if already assigned to this booking
                        // But exclude if locked (in_transit or arrived) or assigned to an ambulance on duty
                        const staffId = s._id || s.id || '';
                        const isLocked = lockedStaffIds.has(staffId);
                        if (isLocked) return false; // Don't show locked staff
                        const isOnDutyAmbulance = staffOnDutyAmbulances.has(staffId);
                        if (isOnDutyAmbulance) return false; // Don't show staff assigned to ambulances on duty
                        const isNotAssigned = !assignedStaffIds.has(staffId);
                        const isCurrentBooking = assigningBooking?.spec.assignedStaffIds?.includes(staffId);

                        return isNotAssigned || isCurrentBooking;
                      });

                      const displayedStaff = filteredStaff.slice(0, displayedStaffLimit);
                      const hasMore = filteredStaff.length > displayedStaffLimit;

                      return (
                        <>
                          <input
                            type="text"
                            placeholder="Search staff by name, phone, role, or category..."
                            value={staffSearchTerm}
                            onChange={(e) => setStaffSearchTerm(e.target.value)}
                            className="search-input"
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              fontSize: '16px',
                              marginBottom: '8px',
                              flexShrink: 0
                            }}
                          />
                          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                            {displayedStaff.map((s) => {
                              const isSelected = assignFormData.assignedStaffIds.includes(s._id || s.id || '');
                              return (
                                <label
                                  key={s._id || s.id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '10px',
                                    cursor: 'pointer',
                                    padding: '4px 0',
                                    lineHeight: '1.5'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setAssignFormData({
                                          ...assignFormData,
                                          assignedStaffIds: [...assignFormData.assignedStaffIds, s._id || s.id || ''],
                                        });
                                      } else {
                                        setAssignFormData({
                                          ...assignFormData,
                                          assignedStaffIds: assignFormData.assignedStaffIds.filter((id) => id !== (s._id || s.id)),
                                        });
                                      }
                                    }}
                                    style={{
                                      marginRight: '10px',
                                      cursor: 'pointer',
                                      width: '18px',
                                      height: '18px',
                                      flexShrink: 0
                                    }}
                                  />
                                  <div
                                    style={{
                                      flex: 1,
                                      userSelect: 'none',
                                      transition: 'color 0.3s'
                                    }}
                                  >
                                    <div style={{ fontWeight: isSelected ? '600' : 'normal' }}>
                                      {s.metadata?.name || s.name}
                                    </div>
                                    {s.spec?.phone && (
                                      <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>
                                        {s.spec.phone}
                                      </div>
                                    )}
                                  </div>
                                </label>
                              );
                            })}
                            {hasMore && (
                              <button
                                type="button"
                                onClick={() => setDisplayedStaffLimit(prev => prev + 50)}
                                className="btn-cancel"
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  marginTop: '8px',
                                  fontSize: '16px'
                                }}
                              >
                                Load More ({filteredStaff.length - displayedStaffLimit} remaining)
                              </button>
                            )}
                            {filteredStaff.length === 0 && (
                              <div
                                className="empty-text"
                                style={{
                                  padding: '20px',
                                  textAlign: 'center',
                                  fontStyle: 'italic'
                                }}
                              >
                                {debouncedStaffSearchTerm ? (
                                  <>No staff found matching "{debouncedStaffSearchTerm}"</>
                                ) : (
                                  <>No staff available</>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Booking Amount */}
                <div className="form-group">
                  <label>Booking Amount (&#8377;)</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={assignFormData.bookingAmount}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setAssignFormData({ ...assignFormData, bookingAmount: value });
                        setAssignAmountManuallyEdited(true);
                      }}
                      placeholder={assignAmountCalculating ? 'Calculating...' : 'Auto or enter manually'}
                      style={{
                        width: '100%',
                        padding: '10px',
                        paddingRight: assignAmountCalculating ? '40px' : '10px',
                        border: isDarkTheme ? '1px solid #475569' : '1px solid #ddd',
                        borderRadius: '6px',
                        backgroundColor: isDarkTheme ? '#1e293b' : 'white',
                        color: isDarkTheme ? '#f1f5f9' : '#000',
                        boxSizing: 'border-box',
                      }}
                    />
                    {assignAmountCalculating && (
                      <span style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '12px',
                        color: isDarkTheme ? '#94a3b8' : '#6b7280',
                      }}>
                        <span className="loading-spinner btn-spinner" aria-hidden />
                      </span>
                    )}
                  </div>
                  {assignCalculatedDistance !== null && assignFormData.ambulanceId && (
                    <p style={{
                      margin: '4px 0 0',
                      fontSize: '12px',
                      color: isDarkTheme ? '#94a3b8' : '#6b7280',
                    }}>
                      Distance: ~{assignCalculatedDistance.toFixed(1)} km
                      {(() => {
                        const selAmb = ambulances.find((a) => a._id === assignFormData.ambulanceId);
                        return selAmb?.spec?.type ? ` | Type: ${getAmbulanceTypeDisplayName(selAmb.spec.type)}` : '';
                      })()}
                      {assignAmountManuallyEdited && (
                        <> &middot; <span
                          style={{ color: isDarkTheme ? '#60a5fa' : '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => setAssignAmountManuallyEdited(false)}
                        >recalculate</span></>
                      )}
                    </p>
                  )}
                </div>
              </div>

              <div className="drawer-footer">
                <button type="button" className="btn-cancel" onClick={handleCloseAssignModal} disabled={assignSubmitting}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={assignSubmitting || !assignFormData.ambulanceId || !assignFormData.pilotId}
                  style={{
                    opacity: (assignSubmitting || !assignFormData.ambulanceId || !assignFormData.pilotId) ? 0.5 : 1,
                    cursor: (assignSubmitting || !assignFormData.ambulanceId || !assignFormData.pilotId) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {assignSubmitting ? (
                    <>
                      <span className="loading-spinner btn-spinner" aria-hidden />
                      Assigning...
                    </>
                  ) : (
                    'Assign'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Booking"
        message={`Are you sure you want to delete the booking "${bookingToDelete?.name}"? This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setBookingToDelete(null);
        }}
      />

      <CancelBookingModal
        isOpen={showCancelBookingConfirm}
        onConfirm={confirmCancelBooking}
        onCancel={() => {
          setShowCancelBookingConfirm(false);
          setBookingToCancel(null);
        }}
      />

      <CancelBookingModal
        isOpen={showCancelPilotAssignmentConfirm}
        onConfirm={confirmCancelPilotAssignment}
        onCancel={() => {
          setShowCancelPilotAssignmentConfirm(false);
          setPilotAssignmentToCancel(null);
        }}
      />

      {/* Booking Details Drawer */}
      {showDetailsDrawer && selectedBooking && (
        <div className="drawer-overlay" onClick={() => setShowDetailsDrawer(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', minWidth: '690px' }}>
            <div
              className="drawer-header"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}
            >
              <h3 className="drawer-title">Booking Details</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {selectedBooking.spec.status !== 'cancelled' && (
                  <button
                    onClick={async () => {
                      const status = selectedBooking.spec.status;
                      const needsLiveLocation = ['assigned', 'in_transit', 'patient_picked', 'patient_dropped'].includes(status);
                      const deviceId = selectedBooking.spec.ambulanceInfo?.deviceId;
                      if (needsLiveLocation && deviceId) {
                        setMapLoading(true);
                        try {
                          const response = await getAmbulanceLiveLocationByDeviceId(deviceId);
                          const data = response.data;
                          if (data?.liveLocation && Array.isArray(data.liveLocation) && data.liveLocation.length === 2) {
                            const [lng, lat] = data.liveLocation;
                            if (lat !== 0 || lng !== 0) {
                              setMapAmbulanceLocation({ latitude: lat, longitude: lng });
                            } else {
                              setMapAmbulanceLocation(null);
                            }
                          } else {
                            setMapAmbulanceLocation(null);
                          }
                        } catch (err) {
                          console.error('Failed to fetch ambulance live location for map:', err);
                          setMapAmbulanceLocation(null);
                        }
                        setMapLoading(false);
                      } else {
                        setMapAmbulanceLocation(null);
                      }
                      setShowBookingMapModal(true);
                    }}
                    disabled={mapLoading}
                    style={{
                      padding: '8px 14px',
                      backgroundColor: '#1976d2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: mapLoading ? 'wait' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      whiteSpace: 'nowrap',
                      opacity: mapLoading ? 0.7 : 1,
                    }}
                    onMouseEnter={(e) => { if (!mapLoading) e.currentTarget.style.backgroundColor = '#1565c0'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#1976d2'; }}
                  >
                    {mapLoading ? 'Loading...' : 'View in Map'}
                  </button>
                )}
                <div style={{ position: 'relative' }} ref={detailsDownloadMenuRef}>
                  <button
                    onClick={() => !detailsDownloading && setShowDetailsDownloadMenu(!showDetailsDownloadMenu)}
                    disabled={detailsDownloading}
                    style={{
                      padding: '8px 14px',
                      backgroundColor: '#4caf50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: detailsDownloading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                      opacity: detailsDownloading ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {detailsDownloading ? 'Generating...' : 'Download'}
                  </button>
                  {showDetailsDownloadMenu && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '5px',
                        backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
                        border: isDarkTheme ? '1px solid #334155' : '1px solid #ddd',
                        borderRadius: '6px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        minWidth: '150px',
                      }}
                    >
                      <button
                        onClick={() => handleDownloadBookingDetails('pdf')}
                        disabled={detailsDownloading}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: 'transparent',
                          color: isDarkTheme ? '#ffffff' : '#333',
                          border: 'none',
                          borderRadius: '6px 6px 0 0',
                          cursor: detailsDownloading ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          textAlign: 'left',
                          opacity: detailsDownloading ? 0.6 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!detailsDownloading) {
                            e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f5f5f5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        Download as PDF
                      </button>
                      <button
                        onClick={() => handleDownloadBookingDetails('excel')}
                        disabled={detailsDownloading}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: 'transparent',
                          color: isDarkTheme ? '#ffffff' : '#333',
                          border: 'none',
                          borderTop: isDarkTheme ? '1px solid #334155' : '1px solid #ddd',
                          borderRadius: '0 0 6px 6px',
                          cursor: detailsDownloading ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          textAlign: 'left',
                          opacity: detailsDownloading ? 0.6 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!detailsDownloading) {
                            e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f5f5f5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        Download as Excel
                      </button>
                    </div>
                  )}
                </div>
                <button className="drawer-close" onClick={() => setShowDetailsDrawer(false)}>
                  ×
                </button>
              </div>
            </div>

            <div className="drawer-body" style={{ padding: '20px' }}>
              {detailsDownloadError && (
                <div style={{ marginBottom: '10px', color: '#f44336', fontSize: '14px' }}>
                  {detailsDownloadError}
                </div>
              )}
              {(() => {
                const booking = selectedBooking;

                // Theme-aware colors
                const cardBg = isDarkTheme ? '#1e293b' : '#f9f9f9';
                const cardBorder = isDarkTheme ? '#334155' : '#e0e0e0';
                const labelColor = isDarkTheme ? '#94a3b8' : '#666';
                const textColor = isDarkTheme ? '#f1f5f9' : '#0f172a';

                const getStatusColor = (status: string) => {
                  switch (status) {
                    case 'pending':
                      return '#ff9800';
                    case 'assigned':
                      return '#2196f3';
                    case 'in_transit':
                      return '#9c27b0';
                    case 'patient_picked':
                      return '#00bcd4';
                    case 'patient_dropped':
                      return '#795548';
                    case 'completed':
                      return '#4caf50';
                    case 'cancelled':
                      return '#fa003f';
                    default:
                      return '#999';
                  }
                };

                const getPriorityColor = (priority: string) => {
                  switch (priority) {
                    case 'critical':
                      return '#f44336';
                    case 'high':
                      return '#ff9800';
                    case 'medium':
                      return '#2196f3';
                    case 'low':
                      return '#4caf50';
                    default:
                      return '#999';
                  }
                };

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Basic Information */}
                    <div>
                      <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Basic Information</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}` }}>
                        <div style={{ fontSize: '16px', color: textColor }}>
                          <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Reference: </span>
                          <span style={{ marginLeft: '16px', fontWeight: '500' }}>{(booking.metadata?.name ?? '').toUpperCase()}</span>
                        </div>
                        <div style={{ fontSize: '16px', color: textColor, display: 'flex', flexWrap: 'nowrap', gap: '16px', alignItems: 'center' }}>
                          <div style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Status: </span>
                            <span style={{ marginLeft: '8px' }}>
                              <span
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  color: 'white',
                                  background: getStatusColor(booking.spec.status),
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {booking.spec.status.replace('_', ' ').toUpperCase()}
                              </span>
                            </span>
                          </div>
                          <div style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Priority: </span>
                            <span style={{ marginLeft: '8px' }}>
                              <span
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  color: 'white',
                                  background: getPriorityColor(booking.spec.priority),
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {booking.spec.priority.toUpperCase()}
                              </span>
                            </span>
                          </div>
                          <div style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Booking Amount: </span>
                            <span style={{ marginLeft: '8px', fontWeight: '600', fontSize: '16px' }}>
                              {booking.spec.bookingAmount ? `₹${Number(booking.spec.bookingAmount).toLocaleString()}` : '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Patient Information */}
                    <div>
                      <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Patient Information</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}` }}>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Patient Name:</label>
                          <div style={{ fontSize: '16px', fontWeight: '500', color: textColor }}>
                            {booking.spec.patientInfo?.name || booking.spec.patientId || '—'}
                          </div>
                        </div>
                        {(booking.spec.patientInfo?.number || booking.spec.patientInfo?.age !== undefined || booking.spec.patientInfo?.dateOfBirth || booking.spec.patientInfo?.gender) && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                            {booking.spec.patientInfo?.number && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Phone:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>{booking.spec.patientInfo.number}</div>
                              </div>
                            )}
                            {(booking.spec.patientInfo?.age !== undefined || booking.spec.patientInfo?.dateOfBirth) && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Age:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>
                                  {(() => {
                                    const age = booking.spec.patientInfo?.age;
                                    const dob = booking.spec.patientInfo?.dateOfBirth;

                                    // If age is 0 or undefined and DOB exists, calculate age in days
                                    if ((age === 0 || age === undefined) && dob) {
                                      const birthDate = new Date(dob);
                                      const today = new Date();
                                      const diffTime = today.getTime() - birthDate.getTime();
                                      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                                      return `${diffDays} days`;
                                    }

                                    // If age is defined and >= 1, show the age value with "years" postfix
                                    if (age !== undefined && age >= 1) {
                                      return `${age} years`;
                                    }

                                    // Fallback
                                    return '—';
                                  })()}
                                </div>
                              </div>
                            )}
                            {booking.spec.patientInfo?.dateOfBirth && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Date of Birth:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>{formatDateDDMMYYYY(booking.spec.patientInfo.dateOfBirth)}</div>
                              </div>
                            )}
                            {booking.spec.patientInfo?.gender && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Gender:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>
                                  {(() => {
                                    const gender = booking.spec.patientInfo.gender?.toLowerCase();
                                    if (gender === 'male') return 'Male';
                                    if (gender === 'female') return 'Female';
                                    if (gender === 'other' || gender === 'others') return 'Others';
                                    // Fallback: capitalize first letter
                                    return booking.spec.patientInfo.gender.charAt(0).toUpperCase() + booking.spec.patientInfo.gender.slice(1).toLowerCase();
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {(booking.spec.patientInfo?.contactPersonName || booking.spec.patientInfo?.contactPersonNumber) && (
                          <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            {booking.spec.patientInfo?.contactPersonName && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Contact Person Name:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>{booking.spec.patientInfo.contactPersonName}</div>
                              </div>
                            )}
                            {booking.spec.patientInfo?.contactPersonNumber && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Contact Person Number:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>{booking.spec.patientInfo.contactPersonNumber}</div>
                              </div>
                            )}
                          </div>
                        )}
                        {booking.spec.medicalCondition && (
                          <div style={{ marginTop: '12px' }}>
                            <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Medical Condition:</label>
                            <div style={{ fontSize: '16px', color: textColor }}>{booking.spec.medicalCondition}</div>
                          </div>
                        )}
                        {/* {booking.spec.bookingAmount && (
                          <div style={{ marginTop: '12px' }}>
                            <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Booking Amount:</label>
                            <div style={{ fontSize: '16px', color: textColor, fontWeight: 600 }}>&#8377; {Number(booking.spec.bookingAmount).toLocaleString()}</div>
                          </div>
                        )} */}
                      </div>
                    </div>

                    {/* Assignment Information */}
                    <div>
                      <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Assignment Information</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}` }}>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Booked By:</label>
                          <div style={{ fontSize: '16px', color: textColor }}>{getBookedByName(booking.spec.bookedBy, booking.spec.bookedByDisplay)}</div>
                        </div>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Ambulance:</label>
                          <div style={{ fontSize: '16px', color: textColor, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                            {booking.spec.ambulanceInfo
                              ? (() => {
                                const name = booking.spec.ambulanceInfo.name;
                                const licensePlate = booking.spec.ambulanceInfo.licensePlate;
                                const type = booking.spec.ambulanceInfo.type;

                                if (!name && !licensePlate && !type) return '—';

                                const nameDisplay = name
                                  ? (licensePlate ? `${name} (${licensePlate})` : name)
                                  : (licensePlate ? `(${licensePlate})` : '—');

                                return (
                                  <>
                                    <div style={{ flex: 1 }}>{nameDisplay}</div>
                                    {type && (
                                      <span
                                        style={{
                                          fontSize: '14px',
                                          padding: '3px 6px',
                                          borderRadius: '4px',
                                          backgroundColor: 'transparent',
                                          color: labelColor,
                                          border: `1.5px solid ${isDarkTheme ? '#334155' : '#cbd5e1'}`,
                                          fontWeight: 500,
                                          display: 'inline-block',
                                          flexShrink: 0,
                                        }}
                                      >
                                        {getAmbulanceTypeDisplayName(type)}
                                      </span>
                                    )}
                                  </>
                                );
                              })()
                              : '—'}
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Pilot/Driver:</label>
                          <div style={{ fontSize: '16px', color: textColor }}>
                            {booking.spec.pilotInfo?.name
                              ? (booking.spec.pilotInfo?.number
                                ? `${booking.spec.pilotInfo.name} (${booking.spec.pilotInfo.number})`
                                : booking.spec.pilotInfo.name)
                              : '—'}
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Assigned Staff:</label>
                          <div style={{ fontSize: '16px', color: textColor }}>
                            {booking.spec.assignedStaffInfo && booking.spec.assignedStaffInfo.length > 0 ? (
                              booking.spec.assignedStaffInfo.map((s, idx) => (
                                <div key={idx} style={{ marginBottom: '4px' }}>
                                  {s.name
                                    ? (s.number
                                      ? `${s.name} (${s.number})`
                                      : s.name)
                                    : '—'}
                                </div>
                              ))
                            ) : (
                              '—'
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Location Information */}
                    <div>
                      <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Location Information</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}` }}>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Pickup Location:</label>
                          <div style={{ fontSize: '16px', color: textColor }}>
                            {booking.spec.pickupLocation?.address || (
                              booking.spec.pickupLocation
                                ? `${booking.spec.pickupLocation.latitude.toFixed(6)}, ${booking.spec.pickupLocation.longitude.toFixed(6)}`
                                : '—'
                            )}
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Dropoff Location:</label>
                          <div style={{ fontSize: '16px', color: textColor }}>
                            {booking.spec.dropoffLocation?.address || (
                              booking.spec.dropoffLocation
                                ? `${booking.spec.dropoffLocation.latitude.toFixed(6)}, ${booking.spec.dropoffLocation.longitude.toFixed(6)}`
                                : '—'
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Timings */}
                    <div>
                      <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Timings</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}` }}>
                        <div style={{ fontSize: '16px', color: textColor }}>
                          <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Booking Time: </span>
                          <span style={{ marginLeft: '16px' }}>
                            {booking.spec.timings?.bookingTime
                              ? formatDateTimeDDMMYYYY(booking.spec.timings.bookingTime)
                              : booking.metadata.createdAt
                                ? formatDateTimeDDMMYYYY(booking.metadata.createdAt)
                                : '—'}
                          </span>
                        </div>
                        <div style={{ fontSize: '16px', color: textColor }}>
                          <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Last Update: </span>
                          <span style={{ marginLeft: '16px' }}>
                            {booking.metadata.updatedAt ? formatDateTimeDDMMYYYY(booking.metadata.updatedAt) : '—'}
                          </span>
                        </div>
                        {booking.spec.timings?.assignedTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Assigned Time: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.assignedTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.inTransitTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Trip Start: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.inTransitTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.scheduledPickupTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Scheduled Pickup: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.scheduledPickupTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.estimatedArrivalTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Estimated Arrival: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.estimatedArrivalTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.actualArrivalTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Actual Arrival: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.actualArrivalTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.completionTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Completion Time: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.completionTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.cancelledTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Cancelled Time: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.cancelledTime)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Admin cancellation (when booking cancelled by tenant admin) */}
                    {booking.spec.status === 'cancelled' && booking.spec.cancellationReason && (
                      <div>
                        <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Cancellation Reason (Admin)</h4>
                        <div style={{ padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}`, fontSize: '16px', color: textColor }}>
                          {booking.spec.cancellationReason}
                        </div>
                      </div>
                    )}

                    {/* Pilot Assignment History */}
                    {booking.spec.pilotHistory && booking.spec.pilotHistory.length > 0 && (
                      <div>
                        <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Pilot Assignment History</h4>
                        <div style={{ padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}`, maxHeight: '300px', overflowY: 'auto' }}>
                          {booking.spec.pilotHistory.map((history, index) => {
                            const getStatusColor = (status: string) => {
                              switch (status) {
                                case 'assigned': return '#2196f3';
                                case 'in_transit': return '#9c27b0';
                                case 'patient_picked': return '#ff9800';
                                case 'patient_dropped': return '#795548';
                                case 'completed': return '#4caf50';
                                case 'cancelled': return '#fa003f';
                                default: return '#666';
                              }
                            };

                            const formatTime = (timestamp?: number) => {
                              if (!timestamp) return '—';
                              return formatDateTimeDDMMYYYY(timestamp);
                            };

                            return (
                              <div
                                key={index}
                                style={{
                                  padding: '12px',
                                  marginBottom: index < booking.spec.pilotHistory!.length - 1 ? '12px' : '0',
                                  borderBottom: index < booking.spec.pilotHistory!.length - 1 ? `1px solid ${cardBorder}` : 'none',
                                  backgroundColor: index === booking.spec.pilotHistory!.length - 1 ? (isDarkTheme ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)') : 'transparent',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                  <div style={{ fontWeight: '600', fontSize: '17px', color: textColor }}>{history.name}</div>
                                  <span
                                    style={{
                                      padding: '4px 10px',
                                      borderRadius: '4px',
                                      fontSize: '13px',
                                      fontWeight: 'bold',
                                      color: 'white',
                                      background: getStatusColor(history.status),
                                    }}
                                  >
                                    {history.status.replace('_', ' ').toUpperCase()}
                                  </span>
                                </div>
                                {history.phone && (
                                  <div style={{ fontSize: '15px', color: labelColor, marginBottom: '6px' }}>
                                    Phone: {history.phone}
                                  </div>
                                )}
                                <div style={{ fontSize: '14px', color: labelColor, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                  {history.assignedTime && <div>Assigned: {formatTime(history.assignedTime)}</div>}
                                  {history.inTransitTime && <div>In Transit: {formatTime(history.inTransitTime)}</div>}
                                  {(history.patientPickedTime ?? history.arrivedTime) && <div>Patient Picked: {formatTime(history.patientPickedTime ?? history.arrivedTime)}</div>}
                                  {(history as { patientDroppedTime?: number }).patientDroppedTime && <div>Patient Dropped: {formatTime((history as { patientDroppedTime?: number }).patientDroppedTime)}</div>}
                                  {history.completionTime && <div>Completed: {formatTime(history.completionTime)}</div>}
                                  {history.cancelledTime && <div>Cancelled: {formatTime(history.cancelledTime)}</div>}
                                  {history.status === 'cancelled' && history.cancellationReason && (
                                    <div style={{ marginTop: '6px', fontStyle: 'italic' }}>Reason: {history.cancellationReason}</div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {booking.spec.notes && (
                      <div>
                        <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Notes</h4>
                        <div style={{ fontSize: '16px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}`, whiteSpace: 'pre-wrap', color: textColor }}>
                          {booking.spec.notes}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Booking Map Modal */}
      {showBookingMapModal && selectedBooking && (() => {
        const booking = selectedBooking;
        const status = booking.spec.status;
        const bookingType = booking.spec.bookingType;
        const pickup = booking.spec.pickupLocation;
        const dropoff = booking.spec.dropoffLocation;

        // Determine icons based on booking type
        const pickupIcon = bookingType === 'from_hospital' ? '/images/hospital_icon.png' : '/images/patient_icon.png';
        const dropoffIcon = bookingType === 'from_hospital' ? '/images/patient_icon.png' : '/images/hospital_icon.png';

        // Get ambulance icon: prefer type icon when available, else default
        let ambulanceIcon = '/images/ambulance_icon.png';
        if (booking.spec.ambulanceInfo?.type) {
          const matchedType = ambulanceTypesForForm.find(
            (t) => t.displayName === booking.spec.ambulanceInfo?.type || t.name === booking.spec.ambulanceInfo?.type
          );
          if (matchedType?.iconUrl) {
            ambulanceIcon = matchedType.iconUrl;
          }
        }

        // Get ambulance live location from the freshly fetched data
        const ambulanceLiveLocation = ['assigned', 'in_transit', 'patient_picked', 'patient_dropped'].includes(status) ? mapAmbulanceLocation : null;

        // Determine what to show based on status
        const showAmbulance = ['assigned', 'in_transit', 'patient_picked', 'patient_dropped'].includes(status) && !!ambulanceLiveLocation;
        const showRoute = status === 'in_transit' || status === 'patient_picked' || status === 'patient_dropped';
        // in_transit: route to pickup; patient_picked/patient_dropped: route to dropoff
        const routeOrigin = showRoute && ambulanceLiveLocation ? ambulanceLiveLocation : null;
        const routeDestination = showRoute
          ? (status === 'in_transit' ? pickup : dropoff)
          : null;

        // Calculate map center
        const centerLat = pickup ? pickup.latitude : (dropoff ? dropoff.latitude : 20.5937);
        const centerLng = pickup ? pickup.longitude : (dropoff ? dropoff.longitude : 78.9629);

        return (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onClick={() => setShowBookingMapModal(false)}
          >
            <div
              style={{
                width: '90vw', maxWidth: '1100px', height: '80vh',
                backgroundColor: isDarkTheme ? '#1e293b' : '#fff',
                borderRadius: '12px', overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div style={{
                padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e0e0e0',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: isDarkTheme ? '#f1f5f9' : '#1a1a1a' }}>
                    Booking Route - {booking.metadata.name}
                  </h3>
                  <span style={{
                    padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, color: '#fff',
                    backgroundColor: status === 'pending' ? '#ff9800' : status === 'assigned' ? '#2196f3'
                      : status === 'in_transit' ? '#9c27b0' : status === 'patient_picked' ? '#00bcd4' : status === 'patient_dropped' ? '#795548'
                        : status === 'completed' ? '#4caf50' : '#999',
                  }}>
                    {status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <button
                  onClick={() => setShowBookingMapModal(false)}
                  style={{
                    background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer',
                    color: isDarkTheme ? '#94a3b8' : '#666', lineHeight: 1,
                  }}
                >×</button>
              </div>

              {/* Map Container */}
              <div style={{ flex: 1, position: 'relative' }}>
                <BookingMapView
                  pickup={pickup}
                  dropoff={dropoff}
                  pickupIcon={pickupIcon}
                  dropoffIcon={dropoffIcon}
                  ambulanceLocation={showAmbulance ? ambulanceLiveLocation : null}
                  ambulanceIcon={ambulanceIcon}
                  showRoute={showRoute}
                  routeOrigin={routeOrigin}
                  routeDestination={routeDestination}
                  centerLat={centerLat}
                  centerLng={centerLng}
                  bookingType={bookingType}
                />
                {/* Legend */}
                <div style={{
                  position: 'absolute', bottom: '16px', left: '16px',
                  backgroundColor: isDarkTheme ? 'rgba(30,41,59,0.92)' : 'rgba(255,255,255,0.95)',
                  borderRadius: '8px', padding: '10px 14px', fontSize: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  color: isDarkTheme ? '#f1f5f9' : '#333',
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <img src={pickupIcon} alt="" style={{ width: 20, height: 20 }} />
                      <span>{bookingType === 'from_hospital' ? 'Hospital (Pickup)' : 'Patient (Pickup)'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <img src={dropoffIcon} alt="" style={{ width: 20, height: 20 }} />
                      <span>{bookingType === 'from_hospital' ? 'Patient (Dropoff)' : 'Hospital (Dropoff)'}</span>
                    </div>
                    {showAmbulance && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img src={ambulanceIcon} alt="" style={{ width: 20, height: 20 }} />
                        <span>Ambulance (Live)</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

    </DashboardLayout>
  );
};

/**
 * BookingMapView - renders Google Map with markers and route
 */
const BookingMapView: React.FC<{
  pickup: { latitude: number; longitude: number; address?: string } | null;
  dropoff: { latitude: number; longitude: number; address?: string } | null;
  pickupIcon: string;
  dropoffIcon: string;
  ambulanceLocation: { latitude: number; longitude: number } | null;
  ambulanceIcon: string;
  showRoute: boolean;
  routeOrigin: { latitude: number; longitude: number } | null;
  routeDestination: { latitude: number; longitude: number; address?: string } | null;
  centerLat: number;
  centerLng: number;
  bookingType: string;
}> = ({ pickup, dropoff, pickupIcon, dropoffIcon, ambulanceLocation, ambulanceIcon, showRoute, routeOrigin, routeDestination, centerLat, centerLng }) => {
  const mapContainerRef = React.useRef<HTMLDivElement>(null);
  const markersRef = React.useRef<google.maps.Marker[]>([]);
  const routeRendererRef = React.useRef<any>(null);

  React.useEffect(() => {
    if (!mapContainerRef.current || !window.google?.maps) return;

    const gm = window.google.maps as any;

    // Initialize map
    const map = new gm.Map(mapContainerRef.current, {
      center: new gm.LatLng(centerLat, centerLng),
      zoom: 13,
      mapTypeId: gm.MapTypeId.ROADMAP,
      fullscreenControl: true,
      zoomControl: true,
      streetViewControl: false,
      mapTypeControl: false,
    });

    const bounds = new gm.LatLngBounds();
    const newMarkers: google.maps.Marker[] = [];

    // Helper to create marker
    const addMarker = (lat: number, lng: number, icon: string, title: string, size: number = 40) => {
      const marker = new gm.Marker({
        position: { lat, lng },
        map,
        title,
        icon: {
          url: icon,
          scaledSize: new gm.Size(size, size),
          anchor: new gm.Point(size / 2, size / 2),
        },
      });
      newMarkers.push(marker);
      bounds.extend(new gm.LatLng(lat, lng));
      return marker;
    };

    // Add pickup marker
    if (pickup) {
      addMarker(pickup.latitude, pickup.longitude, pickupIcon, pickup.address || 'Pickup Location');
    }

    // Add dropoff marker
    if (dropoff) {
      addMarker(dropoff.latitude, dropoff.longitude, dropoffIcon, dropoff.address || 'Dropoff Location');
    }

    // Add ambulance marker
    if (ambulanceLocation) {
      addMarker(ambulanceLocation.latitude, ambulanceLocation.longitude, ambulanceIcon, 'Ambulance (Live)', 48);
    }

    markersRef.current = newMarkers;

    // Fit bounds if we have multiple points
    if (newMarkers.length > 1) {
      map.fitBounds(bounds);
      gm.event.addListenerOnce(map, 'idle', () => {
        const currentZoom = map.getZoom();
        if (currentZoom && currentZoom > 16) map.setZoom(16);
      });
    } else if (newMarkers.length === 1) {
      map.setZoom(14);
    }

    // Draw route if needed
    if (showRoute && routeOrigin && routeDestination) {
      const directionsService = new gm.DirectionsService();
      const directionsRenderer = new gm.DirectionsRenderer({
        map,
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: '#1976d2',
          strokeWeight: 5,
          strokeOpacity: 0.8,
        },
      });
      routeRendererRef.current = directionsRenderer;

      directionsService.route(
        {
          origin: { lat: routeOrigin.latitude, lng: routeOrigin.longitude },
          destination: { lat: routeDestination.latitude, lng: routeDestination.longitude },
          travelMode: gm.TravelMode.DRIVING,
        },
        (result: any, status: any) => {
          if (status === gm.DirectionsStatus.OK && result) {
            directionsRenderer.setDirections(result);
          }
        }
      );
    }

    // Cleanup
    return () => {
      newMarkers.forEach((m) => m.setMap(null));
      if (routeRendererRef.current) {
        routeRendererRef.current.setMap(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup, dropoff, ambulanceLocation, showRoute, routeOrigin, routeDestination]);

  return <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />;
};

export default BookingManagement;
